/**
 * @module sagas/workers/Stats.js
 */
import getIn from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import noop from 'lodash/noop';
import partialRight from 'lodash/partialRight';
import { call, cancel, delay, fork, put, race, select, take } from 'redux-saga/effects';

import { FilterNavTypes } from '../../../../../constants/FilterTypeNav';
import Detector from '../../../../../lib/Detector';
import { StatsPageViewNames } from '../../../constants';
import { mapPathToPageView } from '../../../utils';
import * as StatsActions from '../../actions';
import * as StatsConstants from '../../constants';
import * as D from '../../deps';
import * as StatsExternals from '../../externals';
import * as StatsUtils from '../../utils';
import * as StatsHelpers from '../helpers';

const log = D.Logger.child({ component: 'analytics/stats/sagas' });

const spawn = D.taskManagerSpawnFactory(StatsConstants.DOMAIN_NAME);

const LOOP = true;
const LOCAL_STORE_SIZE_LOGGING_INTERVAL = 1000 * 60 * 60 * 24;

export function* initializeStats() {
  // Otherwise we need to make sure we clear all the stats in the store before the
  // rest of the app kicks into gear
  yield put(StatsActions.clearStats());
  yield put(StatsActions.resetSearchSequence());
  yield fork(StatsHelpers.batchEvents);
  yield fork(initializeLinkTracker);
  yield fork(measureFPS);
}

export function* logEvents(events, defaultDataSelector = StatsUtils.getDefaultEventData) {
  const defaultEventData = yield select(defaultDataSelector);
  yield put(StatsActions.addEvents(events, defaultEventData));
}

export function* onExpireValueExchangeReward({ payload: { expirationTrigger, rewardType } }) {
  const playableSourceId = yield select(
    D.getInDomain('rewards.utils.getPlayableSourceId'),
    rewardType,
  );

  const reward = yield select(D.getInDomain('rewards.utils.getReward'), rewardType);
  const stationId = yield select(D.getInDomain('playback.utils.getCurrentStationId', null));
  const isAdvertiserStation = yield select(
    D.getInDomain('playback.utils.getStationIsAdvertiser', false),
    stationId,
  );
  const eventData = {
    accessory_id: null,
    action: StatsConstants.StatsActions.VALUE_EXCHANGE_END,
    bluetooth_device_name: null,
    carousel_position: null,
    correlation_id: reward.get('correlationId'),
    creative_id: reward.get('creativeId'),
    device_model: null,
    duration_seconds: reward.get('durationSeconds'),
    is_advertiser_station: isAdvertiserStation,
    is_pandora_link: false,
    line_id: reward.get('lineId'),
    offer_name: rewardType,
    playable_source_id: playableSourceId,
    promotion_url: null,
  };

  yield put(
    D.getInDomain(
      'analytics.actions.logEvents',
      noop,
    )([
      {
        eventName: StatsConstants.StatsEvents.VALUE_EXCHANGE,
        eventData,
      },
    ]),
  );

  if (expirationTrigger === StatsConstants.RewardsExpirationTriggers.TIME) {
    yield put(
      D.getInDomain(
        'analytics.actions.logEvents',
        noop,
      )([
        {
          eventName: StatsConstants.StatsEvents.FLEX_T1_REWARD_EXPIRATION,
          eventData: {
            reward_type: rewardType,
            reward_credits_remaining: reward.get('numRemaining'),
            expiration_trigger: expirationTrigger,
          },
        },
      ]),
    );
  }
}

export function* onStartStation({
  payload: { componentName, stationId, stationPosition, stationListLength, newStation },
}) {
  const isShuffleActive = yield select(D.getInDomain('playback.utils.isInShuffleMode'));

  // If starting station is shuffle station, we must log quick_mix_play
  if (isShuffleActive) {
    const allShuffled = yield select(D.getInDomain('playback.utils.isInShuffleAllMode'));

    yield D.tryCall(logEvents, [
      {
        eventName: StatsConstants.Events.QUICK_MIX_PLAY,
        eventData: {
          is_own: true,
          type: StatsConstants.Events.QUICK_MIX_PLAY,
          shuffle_type: allShuffled ? 'ALL' : 'MY',
        },
      },
    ]);
  }

  // The following is for all other start station events, unrelated to quick_mix_play

  const audioPlaying = yield select(D.getInDomain('playback.utils.isAudioPlaying'));

  const eventData = {
    index: stationPosition,
    max_index: stationListLength,
    music_playing: audioPlaying,
    new_station: newStation,
    station_id: stationId,
  };

  switch (componentName) {
    case StatsConstants.StatsComponents.SEARCH:
      // log search_access and change_station events
      return yield D.tryCall(logEvents, [
        {
          eventName: StatsConstants.Events.CHANGE_STATION,
          eventData: {
            index: stationPosition,
            max_index: stationListLength,
            music_playing: audioPlaying,
            new_station: newStation,
            station_id: stationId,
            view_mode: StatsConstants.ViewModes.AUTO_COMPLETE,
          },
        },
      ]);

    // now_playing
    case StatsConstants.StatsComponents.STATION_LIST_HANDLER:
      eventData.source = StatsConstants.StatsSources.NOW_PLAYING;
      eventData.view_mode = StatsConstants.ViewModes.STANDARD;
      break;

    // my_stations_existing
    case StatsConstants.StatsComponents.MY_STATIONS_GRID_ITEM:
      eventData.source = StatsConstants.StatsSources.MY_STATIONS_EXISTING;
      eventData.view_mode = StatsConstants.ViewModes.MY_STATIONS_GRID;
      break;

    // my stations recommended?
    case StatsConstants.StatsComponents.STATIONS_YOU_MAY_LIKE:
      eventData.source = StatsConstants.StatsSources.MY_STATIONS_RECOMMENDED;
      eventData.view_mode = StatsConstants.ViewModes.MY_STATIONS_GRID;
      break;

    // backstage_artist
    case StatsConstants.StatsComponents.BACKSTAGE_ARTIST:
      eventData.source = StatsConstants.StatsSources.BACKSTAGE_ARTIST;
      eventData.view_mode = StatsConstants.ViewModes.PROFILE;
      break;

    // backstage_genre
    case StatsConstants.StatsComponents.BACKSTAGE_GENRE:
      eventData.source = StatsConstants.StatsSources.BACKSTAGE_GENRE;
      eventData.view_mode = StatsConstants.ViewModes.PROFILE;
      break;

    // backstage_track
    case StatsConstants.StatsComponents.BACKSTAGE_TRACK:
      eventData.source = StatsConstants.StatsSources.BACKSTAGE_TRACK;
      eventData.view_mode = StatsConstants.ViewModes.PROFILE;
      break;

    // backstage_station
    case StatsConstants.StatsComponents.BACKSTAGE_STATION:
      eventData.source = StatsConstants.StatsSources.BACKSTAGE_STATION;
      eventData.view_mode = StatsConstants.ViewModes.PROFILE;
      break;

    // browse_genre
    case StatsConstants.StatsComponents.GENRE_GRID_ITEM:
      eventData.source = StatsConstants.StatsSources.BROWSE_GENRE;
      eventData.view_mode = StatsConstants.ViewModes.BROWSE;
      break;

    // landing_page
    case StatsConstants.StatsComponents.ONBOARDING:
      eventData.source = StatsConstants.StatsSources.LANDING_PAGE;
      eventData.view_mode = StatsConstants.ViewModes.STANDARD;
      break;

    case StatsConstants.StatsComponents.THUMBS_UP_LIST_ITEM:
      eventData.source = StatsConstants.StatsSources.PROFILE;
      eventData.view_mode = StatsConstants.ViewModes.THUMBS_UP;
      break;

    default:
      if (isEmpty(componentName)) {
        eventData.source = StatsConstants.StatsSources.UNSPECIFIED;
      }
      break;
  }

  /*
   WEB-7504: If componentName is empty and there's no index or max_index value,
   this event was not fired by a user action on a component, so do not log.
   */
  if (isEmpty(componentName) && (!eventData.max_index || !eventData.index)) {
    return;
  }

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.CHANGE_STATION,
      eventData,
    },
  ]);
}

export function* onPauseTrack({ payload: { logPlaybackActionsAnalytics, isSimStreamViolation } }) {
  if (!logPlaybackActionsAnalytics) {
    return;
  }

  const eventData = {
    action: isSimStreamViolation
      ? StatsConstants.StatsActions.SIM_STREAM_PAUSE
      : StatsConstants.StatsActions.PAUSE,
    backgrounded: D.Utils.PageVisibility.isHidden(),
    day: Date.now().toString(),
    source: StatsConstants.StatsSources.WEB_PLAYBACK_CONTROLS,
    userInitiated: !isSimStreamViolation,
  };

  yield call(logPlaybackInteractions, eventData);

  const params = {
    action: isSimStreamViolation
      ? StatsConstants.StatsActions.SIM_STREAM_PAUSE
      : StatsConstants.StatsActions.PAUSE,
    source: StatsConstants.StatsSources.WEB_PLAYBACK_CONTROLS,
    backgrounded: D.Utils.PageVisibility.isHidden(),
    userInitiated: !isSimStreamViolation,
  };

  yield call(logPlaybackInteractions, params);
}

export function* onReplayTrack({ payload: { eventSource, trackToken } }) {
  const track = yield select(D.getInDomain('playback.utils.getDetailedTrack'), trackToken);

  return yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.FLEX_ENGAGEMENT,
      eventData: {
        action: StatsConstants.StatsActions.REPLAY_TAPPED,
        content_type: StatsUtils.mapToHiveValues()[D.Utils.Playlist.getTrackType(track)],
        control_source: eventSource,
        reward_context: yield select(
          D.getInDomain('rewards.utils.getListenerRewardContextForStats', null),
          StatsConstants.StatsActions.REPLAY_TAPPED,
        ),
        reward_credit_available: yield select(
          D.getInDomain('rewards.utils.getNumReplaysAvailable', null),
        ),
      },
    },
  ]);
}

export function* onSearchTermUpdate() {
  yield put(StatsActions.incrementSearchSequence());
}

export function* onSearchUIUpdate({
  payload: { componentName, hasFocus, isFocusEvent, searchActive },
}) {
  const source = componentName || StatsConstants.StatsSources.STATION_LIST_TOP;

  const eventData = {
    page_view: StatsPageViewNames.COLLECTION,
    device_os: yield select(D.Utils.ClientInfo.getDeviceOS),
    client_timestamp_ms: yield select(D.Utils.Time.getNow),
    app_version: yield select(D.Utils.DebugSettings.version),
  };

  const { currentSearchSessionId, currentSearchSequenceNumber } = yield select(
    StatsUtils.getSearchSessionDataFromState,
  );

  // on focus leave in Search (if text has been entered)
  if (currentSearchSequenceNumber > 0 && hasFocus === false && searchActive === false) {
    // reset the search sequence number and sessionId on navigate away
    yield put(StatsActions.resetSearchSequence());

    return yield D.tryCall(logEvents, [
      {
        eventName: StatsConstants.Events.SEARCH_ACTION,
        eventData: {
          ...eventData,
          action: StatsConstants.StatsActions.SEARCH_ACTION_EXIT,
          exit_path: yield select(
            mapPathToPageView,
            D.Utils.Route.getCurrentPathname({
              location: yield select(D.Utils.Route.getRouterCurrentLocation),
            }),
          ),
          search_session_id: currentSearchSessionId,
          sequence_number: currentSearchSequenceNumber,
          view_mode: StatsConstants.ViewModes.AUTO_COMPLETE,
        },
      },
    ]);
  }

  // on focus enter in Search (either via global input, or the search tile)
  yield put(StatsActions.resetSearchSequence());
  yield put(StatsActions.incrementSearchSequence());

  if (isFocusEvent === true || source !== StatsConstants.StatsSources.STATION_LIST_TOP) {
    return yield D.tryCall(logEvents, [
      {
        eventName: StatsConstants.Events.SEARCH_ACTION,
        eventData: {
          ...eventData,
          action: StatsConstants.StatsActions.ACCESS,
          search_session_id: currentSearchSessionId,
          sequence_number: currentSearchSequenceNumber,
          source,
          view_mode: StatsConstants.ViewModes.SEARCH_PROMPT,
        },
      },
    ]);
  }
}

export function* onSearchResultsUpdate(action) {
  yield put(StatsActions.incrementSearchSequence());
  const { currentSearchSessionId, currentSearchSequenceNumber } = yield select(
    StatsUtils.getSearchSessionDataFromState,
  );

  let selected_filter;
  let searchData;
  let eventAction = StatsConstants.StatsActions.SEARCH_ACTION_FULL_SEARCH;
  const { componentName, searchFilter } = action.params;
  if (componentName === StatsConstants.StatsComponents.SEARCH_T3) {
    eventAction = StatsConstants.StatsActions.SEARCH_ACTION_SEARCH;
    selected_filter = searchFilter;
    searchData = yield select(StatsUtils.getSODSearchDataFromState);
  } else {
    searchData = yield select(StatsUtils.getSearchDataFromState);
  }

  const searchResultsMusicIds = StatsHelpers.getMusicIdString(action.response.results);

  return yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.SEARCH_ACTION,
      eventData: {
        action: eventAction,
        client_result_list: searchResultsMusicIds,
        num_results_returned: action.response.numResults.all,
        num_results_shown: action.response.results.length,
        query: searchData.searchTerm,
        search_session_id: currentSearchSessionId,
        selected_filter,
        sequence_number: currentSearchSequenceNumber,
        server_result_list: searchResultsMusicIds,
        view_mode: StatsConstants.ViewModes.SEARCH_RESULTS,
      },
    },
  ]);
}

export function* onFilterChange({ payload: { searchFilter, results } }) {
  yield put(StatsActions.incrementSearchSequence());
  const { currentSearchSessionId, currentSearchSequenceNumber } = yield select(
    StatsUtils.getSearchSessionDataFromState,
  );
  const { searchTerm } = yield select(StatsUtils.getSODSearchDataFromState);
  const numSearchResults = Array.from(results).length;

  return yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.SEARCH_ACTION,
      eventData: {
        action: StatsConstants.StatsActions.SEARCH_FILTER_CHANGED,
        client_result_list: StatsHelpers.getMusicIdString(results),
        num_results_returned: numSearchResults,
        num_results_shown: numSearchResults,
        query: searchTerm,
        search_session_id: currentSearchSessionId,
        selected_filter: searchFilter,
        sequence_number: currentSearchSequenceNumber,
        server_result_list: StatsHelpers.getMusicIdString(results),
        view_mode: StatsConstants.ViewModes.SEARCH_RESULTS,
      },
    },
  ]);
}

export function* onSearchAutoCompleteResultsUpdate(action) {
  yield put(StatsActions.incrementSearchSequence());
  const { currentSearchSessionId, currentSearchSequenceNumber } = yield select(
    StatsUtils.getSearchSessionDataFromState,
  );

  const autocompleteResults = StatsHelpers.getMusicIdString(action.response);

  return yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.SEARCH_ACTION,
      eventData: {
        action: StatsConstants.StatsActions.SEARCH_ACTION_AUTOCOMPLETE,
        client_result_list: autocompleteResults,
        num_results_returned: action.response.length,
        num_results_shown: action.response.length,
        query: action.params.params.q,
        search_session_id: currentSearchSessionId,
        sequence_number: currentSearchSequenceNumber,
        server_result_list: autocompleteResults,
        view_mode: StatsConstants.ViewModes.AUTO_COMPLETE,
      },
    },
  ]);
}

export function* onSearchSelect({ payload }) {
  yield put(StatsActions.incrementSearchSequence());

  const { componentName, results, isPlayAction, stationCode, searchFilter, resultAction } = payload;
  const { currentSearchSessionId, currentSearchSequenceNumber } = yield select(
    StatsUtils.getSearchSessionDataFromState,
  );

  const source = StatsConstants.StatsSources.STATION_LIST_TOP;
  const numSearchResults = Array.from(results).length;
  let view_mode;
  let selected_filter;
  let selected_result_action;

  switch (componentName) {
    case StatsConstants.StatsComponents.SEARCH:
      view_mode = StatsConstants.ViewModes.AUTO_COMPLETE;
      break;
    default:
      view_mode = StatsConstants.ViewModes.SEARCH_RESULTS;
      selected_filter = searchFilter;
      selected_result_action = resultAction;
      break;
  }

  return yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.SEARCH_ACTION,
      eventData: {
        action: StatsConstants.StatsActions.SEARCH_ACTION_SELECT,
        client_result_list: StatsHelpers.getMusicIdString(results),
        index: payload.listIndex,
        new_station: isPlayAction && stationCode && stationCode.length > 0,
        num_results_returned: numSearchResults,
        num_results_shown: numSearchResults,
        query: payload.searchTerm,
        search_session_id: currentSearchSessionId,
        selected_filter,
        selected_result_action,
        selected_result_id: payload.id,
        selected_result_unique_id: stationCode,
        sequence_number: currentSearchSequenceNumber,
        server_result_list: StatsHelpers.getMusicIdString(results),
        source,
        view_mode,
      },
    },
  ]);
}

export function* onSearchClear() {
  yield put(StatsActions.incrementSearchSequence());

  const { currentSearchSessionId, currentSearchSequenceNumber } = yield select(
    StatsUtils.getSearchSessionDataFromState,
  );

  return yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.SEARCH_ACTION,
      eventData: {
        action: StatsConstants.StatsActions.SEARCH_ACTION_CLEAR,
        sequence_number: currentSearchSequenceNumber,
        view_mode: StatsConstants.ViewModes.AUTO_COMPLETE,
        search_session_id: currentSearchSessionId,
      },
    },
  ]);
}

export function* onSearchResultsScroll({ payload }) {
  yield put(StatsActions.incrementSearchSequence());
  const { componentName, results, searchFilter } = payload;
  const { currentSearchSessionId, currentSearchSequenceNumber } = yield select(
    StatsUtils.getSearchSessionDataFromState,
  );

  if (currentSearchSequenceNumber > 1) {
    const source = StatsConstants.StatsSources.STATION_LIST_TOP;
    let view_mode = StatsConstants.ViewModes.AUTO_COMPLETE;
    const numSearchResults = Array.from(results).length;
    let selected_filter;

    switch (componentName) {
      case StatsConstants.StatsComponents.SEARCH:
        view_mode = StatsConstants.ViewModes.AUTO_COMPLETE;
        break;
      default:
        view_mode = StatsConstants.ViewModes.SEARCH_RESULTS;
        selected_filter = searchFilter;
        break;
    }

    return yield D.tryCall(logEvents, [
      {
        eventName: StatsConstants.Events.SEARCH_ACTION,
        eventData: {
          action: StatsConstants.StatsActions.SEARCH_ACTION_SCROLL,
          client_result_list: StatsHelpers.getMusicIdString(results),
          num_results_returned: numSearchResults,
          num_results_shown: numSearchResults,
          query: payload.searchTerm,
          search_session_id: currentSearchSessionId,
          selected_filter,
          sequence_number: currentSearchSequenceNumber,
          server_result_list: StatsHelpers.getMusicIdString(results),
          source,
          view_mode,
        },
      },
    ]);
  }
}

export function* logSearchResultsRendered({ payload: { searchFilter, searchTerm } }) {
  const { currentSearchSessionId, currentSearchSequenceNumber } = yield select(
    StatsUtils.getSearchSessionDataFromState,
  );
  const viewMode = D.Utils.Time.getSearchMeasurementViewModeForQuery(searchTerm);

  if (isEmpty(viewMode)) {
    return;
  }

  const sharedFields = {
    sequence_number: currentSearchSequenceNumber,
    search_session_id: currentSearchSessionId,
    query: searchTerm,
    selected_filter: searchFilter,
  };

  const measures = D.Utils.Time.getSearchMeasurementsForQuery(searchTerm);
  if (!measures) {
    log.info(`No measures found for ${searchTerm} trying to log search performance.`);
    D.Utils.Time.clearSearchMarksAndMeasures();
    return;
  }

  const events = Object.values(D.Constants.App.SearchMeasures).map(val => {
    return {
      eventName: StatsConstants.Events.EVENT_WEB_VIEW_MODE,
      eventData: {
        ui_mode: val,
        view_mode: viewMode,
        [StatsConstants.PerformanceKeys.TTUI_MSEC]: Math.round(measures[val]),
        ...sharedFields,
      },
    };
  });

  D.Utils.Time.clearSearchMarksAndMeasures();

  return yield D.tryCall(logEvents, events);
}

export function* onWebRegistrationEvent({ registrationEventAction }) {
  const isPartnerUpgradeFlow = yield select(D.Selectors.Payments.isPartnerUpgradeFlow);

  const eventData = { action: registrationEventAction };

  if (isPartnerUpgradeFlow) {
    const partnerPrefix = yield select(StatsUtils.getPartnerNamePrefix);
    eventData.source = yield select(D.Selectors.Payments.getPartnerUpgradePartnerName);
    eventData.action_sku_feature_code = yield select(D.Selectors.Payments.getPartnerUpgradeTier);
    eventData.page_view = partnerPrefix + StatsPageViewNames.REGISTRATION;
  }

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.WEB_REGISTRATION,
      eventData,
    },
  ]);
}

export function* onLoginSuccess() {
  yield put(D.Actions.Application.detectAdBlocker('app_start'));

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_WEB_VIEW_MODE,
      eventData: {
        action: StatsConstants.AuthEvents.LOGIN_SUCCESS,
      },
    },
  ]);
}

export function* onRegisterSuccess() {
  yield D.tryCall(onWebRegistrationEvent, {
    registrationEventAction: StatsConstants.WebRegistrationEvents.REGISTRATION_SUCCESSFUL,
  });
}

export function* onRegisterFail(action) {
  switch (action.error.errorType) {
    case D.Constants.ServiceErrors.LISTENER_DUPLICATE_USERNAME: {
      yield D.tryCall(onWebRegistrationEvent, {
        registrationEventAction:
          StatsConstants.WebRegistrationEvents.REGISTRATION_FAILED_EXISTING_EMAIL,
      });
      break;
    }
    case D.Constants.ServiceErrors.BIRTH_YEAR_TOO_YOUNG: {
      yield D.tryCall(onWebRegistrationEvent, {
        registrationEventAction: StatsConstants.WebRegistrationEvents.REGISTRATION_FAILED_UNDER_AGE,
      });
      break;
    }
    default: {
      yield D.tryCall(onWebRegistrationEvent, {
        registrationEventAction: StatsConstants.WebRegistrationEvents.REGISTRATION_FAILED_OTHER,
      });
      break;
    }
  }
}

export function* onUpgradePageEvent({ payload: { action, pageView, viewMode } }) {
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const pageViewData = yield call(getPageView, currentPath);

  const eventData = yield select(StatsUtils.createPartnerUpgradeEventData);
  eventData.action = action;
  eventData.page_view = pageView || getIn(pageViewData, StatsConstants.DefaultFieldNames.PAGE_VIEW);
  eventData.view_mode = viewMode || getIn(pageViewData, StatsConstants.DefaultFieldNames.VIEW_MODE);

  if (eventData.page_view) {
    yield D.tryCall(logEvents, [
      {
        eventName: StatsConstants.Events.UPGRADE_PAGE,
        eventData,
      },
    ]);
  }
}

export function* onUpgradeConfirmationEvent({ payload: { action, page_view, view_mode, source } }) {
  const eventData = yield select(StatsUtils.createPartnerUpgradeEventData);
  eventData.action = action;
  eventData.page_view = page_view;
  eventData.view_mode = view_mode;
  eventData.source = source;

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.UPGRADE_CONFIRMATION,
      eventData,
    },
  ]);
}

export function* onUpdateBillingInfo() {
  yield put(
    StatsActions.logWebViewModeEvent(
      StatsConstants.ViewModes.SUBSCRIPTION,
      StatsConstants.ViewModes.UPDATE_PAYMENT,
    ),
  );
}

export function* onToggleSwitchingPlans({ payload }) {
  if (payload.isSwitchingPlans) {
    yield put(
      StatsActions.logWebViewModeEvent(
        StatsConstants.ViewModes.SUBSCRIPTION,
        StatsConstants.ViewModes.SWITCH_PLANS,
      ),
    );
  }
}

export function* onGetAdlist({ request }) {
  const { content } = request;
  if (!content) {
    return;
  }

  const {
    nextTrackStationId,
    secondsUntilAd,
    currentTrackStationId,
    currentTrackToken,
    currentTrackType,
    currentTrackSecondsPlayed,
    userInitiatedTrackEnd,
    userInitiatedTrackEndActionType,
    secondsSinceDisplayAd,
  } = content;

  const requestString = JSON.stringify({
    currentStationId: currentTrackStationId,
    nextStationId: nextTrackStationId,
    secondsPlayed: currentTrackSecondsPlayed,
    secondsSinceDisplayAd,
    secondsUntilAd,
    testMode: select(D.Utils.TunerVars.getTestMode),
    trackToken: currentTrackToken,
    trackType: currentTrackType,
    userInitTrackEnd: userInitiatedTrackEnd,
    videoAdReady: D.Utils.PageVisibility.isHidden() ? false : true,
  });

  const eventData = {
    accessory_id: null,
    ad_id: null,
    ad_unit_id: null,
    bluetooth_device_name: null,
    correlation_id: null,
    creative_id: null,
    device_model: null,
    event_description: null,
    event_sub_type: getChronosLifeCycleSubTypeFromActionType(userInitiatedTrackEndActionType),
    event_type: 'request',
    is_pandora_link: false,
    line_id: null,
    request_string: requestString,
  };

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.StatsActions.CHRONOS_LIFECYCLE,
      eventData,
    },
  ]);
}

export function* onStationCreated({ payload: { stationId, componentName, createStationParams } }) {
  const { stationCode } = createStationParams;
  const stationKey = stationCode ? stationCode.substr(0, 2) : null;

  const eventData = {
    station_key: stationKey,
    station_id: stationId,
  };

  switch (componentName) {
    // search
    case StatsConstants.StatsComponents.SEARCH:
      eventData.source = StatsConstants.StatsSources.AUTO_COMPLETE;
      eventData.view_mode = StatsConstants.ViewModes.STANDARD;
      break;

    // result rollover
    case StatsConstants.StatsComponents.SEARCH_RESULTS:
      eventData.source = StatsConstants.StatsSources.SEARCH;
      eventData.view_mode = StatsConstants.ViewModes.RESULT_ROLLOVER;
      break;

    // now_playing
    case StatsConstants.StatsComponents.STATION_LIST_HANDLER:
      eventData.view_mode = StatsConstants.ViewModes.STANDARD;
      break;

    // my_stations_existing
    case StatsConstants.StatsComponents.MY_STATIONS_GRID_ITEM:
      eventData.view_mode = StatsConstants.ViewModes.MY_STATIONS_GRID;
      break;

    // my stations recommended?
    case StatsConstants.StatsComponents.STATIONS_YOU_MAY_LIKE:
      eventData.view_mode = StatsConstants.ViewModes.MY_STATIONS_GRID;
      eventData.source = StatsConstants.StatsSources.REC;
      break;

    // backstage_artist
    case StatsConstants.StatsComponents.BACKSTAGE_ARTIST:
      eventData.view_mode = StatsConstants.ViewModes.PROFILE;
      eventData.source = StatsConstants.StatsSources.BACKSTAGE_ARTIST;
      break;

    // backstage_genre
    case StatsConstants.StatsComponents.BACKSTAGE_GENRE:
      eventData.view_mode = StatsConstants.ViewModes.PROFILE;
      eventData.source = StatsConstants.StatsSources.BACKSTAGE_GENRE;
      break;

    // backstage_track
    case StatsConstants.StatsComponents.BACKSTAGE_TRACK:
      eventData.view_mode = StatsConstants.ViewModes.PROFILE;
      eventData.source = StatsConstants.StatsSources.BACKSTAGE_TRACK;
      break;

    // backstage_station
    case StatsConstants.StatsComponents.BACKSTAGE_STATION:
      eventData.view_mode = StatsConstants.ViewModes.PROFILE;
      eventData.source = StatsConstants.StatsSources.BACKSTAGE_STATION;
      break;

    // browse_genre
    case StatsConstants.StatsComponents.GENRE_GRID_ITEM:
      eventData.view_mode = StatsConstants.ViewModes.BROWSE;
      eventData.source = StatsConstants.StatsSources.BROWSE_GENRE;
      break;

    // landing_page
    case StatsConstants.StatsComponents.ONBOARDING:
      eventData.view_mode = StatsConstants.ViewModes.STANDARD;
      eventData.source = StatsConstants.StatsSources.LANDING_PAGE;
      break;

    case StatsConstants.StatsComponents.THUMBS_UP_LIST_ITEM:
      eventData.source = StatsConstants.StatsSources.PROFILE;
      eventData.view_mode = StatsConstants.ViewModes.THUMBS_UP;
      break;

    // js api
    case StatsConstants.StatsComponents.EXTERNAL_API:
      eventData.view_mode = StatsConstants.ViewModes.STANDARD;
      eventData.source = StatsConstants.StatsSources.JS_API;
      break;

    case StatsConstants.StatsComponents.RECENTLY_PLAYED_CAROUSEL:
      eventData.view_mode = StatsConstants.ViewModes.RECENTLY_PLAYED_CAROUSEL;
      break;

    case StatsConstants.StatsComponents.MY_MUSIC: {
      const currentFilter = yield select(D.getInDomain('collection.utils.getCurrentFilter', null));
      eventData.view_mode = currentFilter;
      break;
    }
  }

  if (isUndefined(componentName)) {
    // if there isn't a default source for a given component, let's see if there's a default via station key
    switch (stationKey) {
      case StatsConstants.StatsStationKeys.AUTO_COMPLETE:
        eventData.source = StatsConstants.StatsSources.AUTO_COMPLETE;
        break;

      case StatsConstants.StatsStationKeys.SUGGESTED_ARTIST:
      case StatsConstants.StatsStationKeys.SUGGESTED_GENRE:
        if (componentName === StatsConstants.StatsComponents.STATION_LIST_HANDLER) {
          eventData.source = StatsConstants.StatsSources.REC_STATION_LIST;
          eventData.view_mode = StatsConstants.ViewModes.STANDARD;
        } else {
          eventData.source = StatsConstants.StatsSources.REC;
        }
        break;

      // if there's no componentName, it's likely from a smart_url
      case StatsConstants.StatsStationKeys.MUSIC_ID:
      case StatsConstants.StatsStationKeys.STATION_ID:
        if (!componentName) {
          eventData.source = StatsConstants.StatsSources.SMART_URL;
          eventData.view_mode = StatsConstants.ViewModes.STANDARD;
        }
        break;

      // component isn't listed and source isn't specified
      default:
        // if station_key is null, then we make the eventData.source null as well
        if (!eventData.station_key) {
          eventData.source = null;
        } else {
          eventData.source = StatsConstants.StatsSources.UNSPECIFIED;
        }
        eventData.view_mode = StatsConstants.ViewModes.STANDARD;
        break;
    }
  }

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.CREATE_STATION,
      eventData,
    },
  ]);
}

export function* onStationDeleted({ payload: { stationId, componentName } }) {
  const eventData = {
    station_id: stationId,
  };

  switch (componentName) {
    case StatsConstants.StatsComponents.RECENTLY_PLAYED_CAROUSEL:
      eventData.view_mode = StatsConstants.ViewModes.RECENTLY_PLAYED_CAROUSEL;
      break;

    case StatsConstants.StatsComponents.MY_MUSIC:
    case StatsConstants.StatsComponents.MY_STATIONS: {
      const currentFilter = yield select(D.getInDomain('collection.utils.getCurrentFilter', null));
      eventData.view_mode = currentFilter;
      break;
    }
  }

  yield D.tryCall(logEvents, [
    {
      eventName: D.Constants.Analytics.ActionTypes.DELETE_STATION,
      eventData,
    },
  ]);
}

export function* onExternalApiEvent(action) {
  let sourceId;
  let sourceType;

  try {
    const tracking = action.payload.params.param.tracking;
    sourceId = tracking.sourceId;
    sourceType = tracking.sourceType;
  } catch (_e) {
    return;
  }

  yield put(D.Actions.Payments.upgradeOffered({ sourceType, sourceId }));
}

export function* onIdleMonitorUpdate({ payload }) {
  const isActive =
    payload[
      D.getInDomain('application.constants.IdleMonitorConstants.IdleMonitorKeys.IDLE_STATUS')
    ] === D.getInDomain('application.constants.IdleMonitorConstants.IdleMonitorStatuses.ACTIVE');

  yield call(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_LISTENER_IDLE,
      eventData: {
        active: isActive.toString(),
        action: isActive ? 'none' : '',
      },
    },
  ]); // To be deleted
}

export function* onWebDisplayAdLifecycleEvent(action) {
  yield call(logDisplayAdEvent, [
    {
      eventName: D.Constants.Analytics.ActionTypes.WEB_DISPLAY_AD_LIFECYCLE,
      eventData: action.payload,
      actionType: action.type,
    },
  ]);
}

export function* onCollectionListSorted({ payload: { newDisplayType } }) {
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const eventData = yield call(getPageView, currentPath);
  const isPremiumUser = yield select(D.Utils.Listener.hasPremiumFeatures);

  const isCollection =
    eventData[StatsConstants.DefaultFieldNames.PAGE_VIEW] === StatsPageViewNames.COLLECTION;
  const viewMode = eventData[StatsConstants.DefaultFieldNames.VIEW_MODE];
  let isStation = viewMode === D.Constants.FilterNav.FilterNavTypes.STATIONS;

  const RECENT = D.Constants.MyStations.MyStationFilterTypes.RECENT;
  const RECENTLY_PLAYED = D.Constants.MyStations.MyStationSortTypes.RECENT;

  if (!isPremiumUser && isCollection && isUndefined(viewMode)) {
    eventData.view_mode = D.Constants.FilterNav.FilterNavTypes.STATIONS;
    isStation = true;
  }

  if (isCollection && isStation && newDisplayType === RECENT) {
    eventData.mode = RECENTLY_PLAYED;
  } else {
    eventData.mode =
      newDisplayType === D.Constants.MyStations.MyStationSortTypes.A_TO_Z
        ? D.Constants.MyStations.AtoZSort
        : newDisplayType;
  }

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.WEB_SORT,
      eventData,
    },
  ]);
}

export function* onBrowserAutomaticPlayback({ payload }) {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_WEB_AUTOMATIC_PLAYBACK,
      eventData: payload,
    },
  ]);
}

export function* logPremiumPlaylistUnlocked({ payload }) {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_PLAYLIST,
      eventData: payload,
    },
  ]);
}

export function* logPremiumPlaylistCoachmark({ payload: { reason } }) {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.COACHMARK,
      eventData: {
        coachmark_id: StatsExternals.CoachmarkIds.LINKED_PLAYLIST,
        coachmark_type: StatsConstants.CoachmarkTypes.ONBOARDING,
        reason,
      },
    },
  ]);
}

export function* logAdBlockerAudioMessageCoachmark() {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.COACHMARK,
      eventData: {
        coachmark_id: StatsExternals.CoachmarkIds.AD_BLOCKER_DETECTED_WARNING_AUDIO,
        coachmark_type: StatsConstants.CoachmarkTypes.AD_BLOCKER,
        reason: 'audio_played',
        clicked: false,
      },
    },
  ]);
}

export function* onWebViewModeInteraction({ payload }) {
  const { event_name, ...eventPayload } = payload;
  const eventName = event_name || StatsConstants.Events.EVENT_WEB_VIEW_MODE;

  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  let PAGE_VIEW = payload.page_view;
  if (!PAGE_VIEW || !payload.view_mode) {
    const eventData = yield call(getPageView, currentPath);
    PAGE_VIEW = PAGE_VIEW || eventData.page_view || '';
    payload.view_mode = payload.view_mode || eventData.view_mode || '';
  }
  const isInPartnerUpgradeFlow = yield select(D.Selectors.Payments.isPartnerUpgradeFlow);
  const partnerPrefix = isInPartnerUpgradeFlow ? yield select(StatsUtils.getPartnerNamePrefix) : '';

  const eventData = {
    ...eventPayload,
    page_view: partnerPrefix + PAGE_VIEW,
  };

  yield D.tryCall(logEvents, [{ eventName, eventData }]);
}

export const trackAnonGoToRegister = (() => {
  let prevLocation = D.Utils.Route.getRootPath();
  return function* trackAnonGoToRegisterHandler(action) {
    const isAnon = yield select(D.Utils.Listener.isAnonymousListener);
    if (!isAnon) {
      return;
    }

    const isRegistrationPage = D.Utils.Route.isRegisterPath(action.payload.pathname);
    if (isRegistrationPage) {
      // Make sure we don't log clicks from Onboarding page (i.e. 'root path') since that is being done in logAnonymousStartTrialClick() now (WEBCLIENT-5892)
      if (
        prevLocation !== D.Utils.Route.getRootPath() &&
        !D.Utils.Route.isUpgradeOrSettingsUpgradePage(prevLocation)
      ) {
        yield D.tryCall(
          onWebRegistrationEvent,
          StatsConstants.WebRegistrationEvents.SIGN_UP_CLICKED_FROM_ANON_LISTENING,
        );
      }
    }
    // eslint-disable-next-line require-atomic-updates
    prevLocation = action.payload.pathname;
  };
})();

export function* trackLocationChange(action) {
  const actionMethod = action && action.payload && action.payload.action;
  if (
    actionMethod !== D.Constants.App.RouteActions.PUSH &&
    actionMethod !== D.Constants.App.RouteActions.REPLACE
  ) {
    /* only care about push & replace */
    return;
  }
  yield D.tryCall(logLocationChange, action);
}

export function* trackBrowseSession(action) {
  // If we've landed on Browse or navigated to it from somewhere other than Browse
  // we need to generate a new Browse session ID
  const currentPath = action.payload.pathname;
  const previousPath = yield select(D.getInDomain('application.utils.getPreviousPath'));
  if (!D.Utils.Route.isBrowsePath(previousPath) && D.Utils.Route.isBrowsePath(currentPath)) {
    yield put(StatsActions.generateBrowseSessionId());
  }
}

export function* onLocationChange(action) {
  yield spawn(trackLocationChange, action);
  yield spawn(trackAnonGoToRegister, action);
  yield spawn(trackBrowseSession, action);
}

export function* onApplicationInitialize() {
  const size = yield select(D.getStoreSize);
  const persistedSize = yield call(D.getPersistedStoreSize);
  yield D.tryCall(logLocalStoreSize, size, persistedSize);

  yield spawn(logAppTimings, D.Utils.Time.getTimingMeasurements());
  yield spawn(monitorStoreSize);
}

export function* onSkipTrack(action) {
  const hasStationSkips = !(yield select(
    D.Utils.Skip.hasListenerReachedStationSkipLimit,
    action.data.stationId,
  ));
  const rewardContext = yield select(
    D.getInDomain('rewards.utils.getListenerRewardContextForStats', null),
    StatsConstants.StatsActions.SKIP_TAPPED,
    hasStationSkips,
  );
  const numSkipsAvailable = yield select(D.getInDomain('rewards.utils.getNumSkipsAvailable', null));
  const track = yield select(
    D.getInDomain('playback.utils.getCurrentTrackFromActivePlaylist', null),
  );
  const eventData = {
    action: StatsConstants.StatsActions.SKIP_TAPPED,
    content_type: StatsUtils.mapToHiveValues()[D.Utils.Playlist.getTrackType(track)],
    control_source: StatsConstants.StatsSources.OTHER,
    reward_context: rewardContext,
    reward_credit_available: numSkipsAvailable,
  };

  return yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.FLEX_ENGAGEMENT,
      eventData,
    },
  ]);
}

export function* logDisplayAdEvent(events) {
  const length = events.length;
  if (length === 1) {
    const eventName = events[0].actionType;
    yield D.tryCall(logEvents, events, partialRight(StatsUtils.getDisplayAdEventData, eventName));
  }
}

export function* getPremiumAccessCoachmarkData() {
  const openCoachmarkData = yield select(D.getInDomain('rewards.utils.getOpenCoachmarkData'));
  return openCoachmarkData.getIn(['premiumAccessCoachmarkData', 'coachmarkData']);
}

export function* logPremiumAccessCoachmarkEvent(reason, clicked) {
  const data = yield call(getPremiumAccessCoachmarkData);
  if (!data) {
    // If not a Premium Access coachmark
    return;
  }

  let source = data.get('target');
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const pageViewData = yield call(getPageView, currentPath);
  const pageView = pageViewData[StatsConstants.DefaultFieldNames.PAGE_VIEW];
  if (
    pageView === StatsPageViewNames.BACKSTAGE_ALBUM &&
    source === D.Constants.SourceTypes.PandoraTypes.SONG
  ) {
    source = D.Constants.SourceTypes.PandoraTypes.ALBUM;
  }
  const context = data.get('context');
  const openedFromSearch =
    !!context && context === D.Constants.Ads.Rewards.ContextTypes.SEARCH_PLAY;

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.COACHMARK,
      eventData: {
        coachmark_id: StatsUtils.getCoachmarkId(source, openedFromSearch),
        coachmark_type: StatsConstants.CoachmarkTypes.PREMIUM_ACCESS,
        reason,
        clicked,
      },
    },
  ]);
}

export function* logDismissedPremiumAccessCoachmark(action) {
  const reason = getPACoachmarkDismissEventReason(action);
  yield call(logPremiumAccessCoachmarkEvent, reason, true);
}

export function* logOpenedPremiumAccessCoachmark() {
  yield call(logPremiumAccessCoachmarkEvent, null, false);
}

export function* logCTAPremiumAccessCoachmark() {
  yield call(
    logPremiumAccessCoachmarkEvent,
    StatsExternals.CoachmarkReasons.CLICK_THROUGH_ACTION_CLICKED,
    true,
  );
}

export function* logPlayedPremiumAccessWelcomeAudio({ payload }) {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.COACHMARK,
      eventData: {
        coachmark_id: payload,
        coachmark_type: StatsConstants.CoachmarkTypes.ONBOARDING,
        reason: '',
      },
    },
  ]);
}

export function* logOpenedLTUXPAUpsellCoachmark() {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.COACHMARK,
      eventData: {
        coachmark_id: StatsExternals.CoachmarkIds.LTUX_UPSELL,
        coachmark_type: StatsConstants.CoachmarkTypes.UPSELL,
        reason: '',
        clicked: false,
      },
    },
  ]);
}

export function* logClickedLTUXPAUpsellCoachmark() {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.COACHMARK,
      eventData: {
        coachmark_id: StatsExternals.CoachmarkIds.LTUX_UPSELL,
        coachmark_type: StatsConstants.CoachmarkTypes.UPSELL,
        reason: StatsExternals.CoachmarkReasons.UPSELL_CLICK,
        clicked: true,
      },
    },
  ]);
}

export function* logClickedStartTrialPAUpsellCoachmark() {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.COACHMARK,
      eventData: {
        coachmark_id: StatsExternals.CoachmarkIds.ALBUM_BACKSTAGE,
        coachmark_type: StatsConstants.CoachmarkTypes.PREMIUM_ACCESS,
        reason: StatsExternals.CoachmarkReasons.UPGRADE,
        clicked: true,
      },
    },
  ]);
}

export function* logPAResumeCoachmarkEvent(reason, clicked) {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.COACHMARK,
      eventData: {
        coachmark_id: StatsExternals.CoachmarkIds.RESUME_INTERACTIVE_VIDEO,
        coachmark_type: StatsConstants.CoachmarkTypes.IN_VIDEO,
        reason,
        clicked,
      },
    },
  ]);
}

export function* logOpenedPAResumeCoachmark() {
  yield call(logPAResumeCoachmarkEvent, null, false);
}

export function* logResumedFromPAResumeCoachmark() {
  yield call(
    logPAResumeCoachmarkEvent,
    StatsExternals.CoachmarkReasons.CLICK_THROUGH_ACTION_CLICKED,
    true,
  );
}

export function* logDismissedFromPAResumeCoachmark(action) {
  if (action.data.isTimeout) {
    return;
  }
  yield call(logPAResumeCoachmarkEvent, StatsExternals.CoachmarkReasons.TOUCH, true);
}

export function* logLocalStoreSize(sizeInBytes, persistedSizeInBytes) {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.WEB_STORE_SIZE,
      eventData: {
        size_in_bytes: sizeInBytes,
        persisted_size_in_bytes: persistedSizeInBytes,
      },
    },
  ]);
}

export function* logAppTimings(eventData) {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.APP_TIMINGS,
      eventData,
    },
  ]);
}

export function* logPlaybackInteractions({
  action,
  userInitiated,
  source,
  backgrounded = D.Utils.PageVisibility.isHidden(),
}) {
  const currentStationId = yield select(D.getInDomain('playback.utils.getCurrentStationId'));
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const pageViewData = yield call(getPageView, currentPath);

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.PLAYBACK_INTERACTIONS,
      eventData: {
        action,
        backgrounded,
        control_source: source,
        station_id: currentStationId,
        user_initiated: userInitiated,
        ...pageViewData,
      },
    },
  ]);
}

export function* logAudioPlayerTTA(data) {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.PLAYBACK_INTERACTIONS,
      eventData: {
        action: StatsConstants.StatsActions.AUDIOPLAYER_TIME_TO_AUDIO,
        ...data,
      },
    },
  ]);
}

export function* logAudioTrackFullMetrics({ payload }) {
  const { PLAYBACK_METRICS, TRACK_METADATA } = payload;

  if (!PLAYBACK_METRICS || !PLAYBACK_METRICS.records.length) {
    log.info(`No metrics found to log`);
    return;
  }

  const trackType = TRACK_METADATA.trackType;
  const isEncrypted = TRACK_METADATA.isEncrypted;
  const trackId = TRACK_METADATA.trackId;
  const isAPS = TRACK_METADATA.isAPS;

  const events = PLAYBACK_METRICS.records.map(val => ({
    eventName: StatsConstants.Events.AUDIO_TRACK_PLAYBACK,
    eventData: {
      action: val.type,
      audio_url: val.trackId,
      content_type: trackType,
      is_aps: isAPS,
      is_encrypted: isEncrypted,
      track_id: trackId,
      ...val,
    },
  }));
  yield D.tryCall(logEvents, events);
}

export function* logAudioPlayerTTAPreloaded(data) {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.PLAYBACK_INTERACTIONS,
      eventData: {
        action: StatsConstants.StatsActions.AUDIOPLAYER_TIME_TO_AUDIO_PRELOADED,
        ...data,
      },
    },
  ]);
}

export function* skipLimit() {
  const currentStationId = yield select(D.getInDomain('playback.utils.getCurrentStationId'));

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.SKIP_LIMIT,
      eventData: {
        station_id: currentStationId,
        at_daily_skip_limit: yield select(D.Utils.Skip.hasListenerReachedDailySkipLimit),
        at_station_skip_limit: yield select(
          D.Utils.Skip.hasListenerReachedStationSkipLimit,
          currentStationId,
        ),
      },
    },
  ]);
}

/**
 * Logs track end for stations
 *
 * @param {string} reason
 * @param {object} [track] - Only necessary if different than the currently playing track
 */
export function* onTrackEndStation(reason, track) {
  const state = D.getStore().getState();
  // We don't log to this table for audio ads, check for that first
  const isAudioAd = D.getInDomain('ads.utils.isAudioAd', false)(state, track);

  if (isAudioAd) {
    return;
  }

  const currentStationId = yield select(D.getInDomain('playback.utils.getCurrentStationId'));
  let endedTrack;
  let remainingSeconds = null;
  let elapsedTime = null;
  let spinType = StatsConstants.TrackEndSpinTypes.RADIO;

  if (track) {
    endedTrack = track;
  } else {
    const currentTrackToken = yield select(D.getInDomain('playback.utils.getCurrentTrackToken'));
    endedTrack = yield select(D.Utils.Playlist.getTrackById, currentTrackToken);
  }

  // Expired tracks were never played so they don't have duration, elapsedTime nor can they be replays
  if (reason !== StatsConstants.TrackEndReasons.EXPIRED) {
    if (endedTrack) {
      const trackLength = Math.floor(endedTrack.get('trackLength'));
      elapsedTime = Math.floor(
        yield select(D.getInDomain('playback.utils.getPlaylistTrackTime'), currentStationId),
      );

      // Elapsed time can be a bit delayed sometimes, resulting in a value that is higher than the song duration
      if (elapsedTime > trackLength) {
        elapsedTime = trackLength;
      }

      remainingSeconds = trackLength - elapsedTime;
    }

    const isReplayedTrack = yield select(D.getInDomain('playback.utils.isNowPlayingReplayedTrack'));
    if (isReplayedTrack) {
      spinType = StatsConstants.TrackEndSpinTypes.REPLAY;
    }
  }

  // If this was a skip or replay log reward state
  let rewardStateReplays = null;
  let rewardStateSkips = null;
  if (
    reason === StatsConstants.TrackEndReasons.REPLAY ||
    reason === StatsConstants.TrackEndReasons.SKIP
  ) {
    const hasStationSkips = !(yield select(
      D.Utils.Skip.hasListenerReachedStationSkipLimit,
      currentStationId,
    ));
    try {
      rewardStateReplays = yield select(
        D.getInDomain('rewards.utils.getListenerRewardContextForStats', null),
        StatsConstants.StatsActions.REPLAY_TAPPED,
      );
      rewardStateSkips = yield select(
        D.getInDomain('rewards.utils.getListenerRewardContextForStats', null),
        StatsConstants.StatsActions.SKIP_TAPPED,
        hasStationSkips,
      );
    } catch (error) {
      if (error instanceof D.Errors.MissingUtilError) {
        // No rewards for t2/t3
        rewardStateReplays = 0;
        rewardStateSkips = 0;
      }
    }
  }

  const data = {
    audio_token: D.Utils.Playlist.getTrackAudioToken(endedTrack),
    elapsed_seconds: elapsedTime,
    offline: 'false', // Always false for web
    reason,
    remaining_seconds: remainingSeconds,
    reward_state_replays: rewardStateReplays,
    reward_state_skips: rewardStateSkips,
    spin_type: spinType,
    station_id: currentStationId,
    track_uid: D.Utils.Playlist.getTrackMusicId(endedTrack),
  };

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.TRACK_END,
      eventData: data,
    },
  ]);
}

// this worker is used as a funnel point for track ends that could be from either
// stations or on demand sources. It will determine whether to call the ondemand worker
// or the station worker.
export function* onTrackEnd({ reason, track, token, playlistId, playlistType }) {
  let endedTrack;
  let isNonAdInterruption;
  let isAudioAd;
  let isMusicTrack;

  if (track) {
    endedTrack = track;
  } else if (token) {
    endedTrack = yield select(
      D.getInDomain('playback.utils.resolveAudioTrackForPlaylist'),
      token,
      playlistId,
    );
  }

  const trackType = endedTrack.get('trackType') || endedTrack.get('type'); // trackType for APS items is 'type'
  switch (trackType) {
    case D.Constants.TrackType.TrackType.ARTIST_MESSAGE:
    case D.Constants.TrackType.TrackType.AUDIO_WARNING:
      isNonAdInterruption = true;
      break;
    case D.Constants.Ads.Audio.Constants.AUDIO_AD_TRACK_TYPE:
      isAudioAd = true;
      break;
    case D.Constants.TrackType.TrackType.TRACK:
      isMusicTrack = true;
      break;
  }

  if (isNonAdInterruption) {
    yield put(StatsActions.ttmNonAdInterruption());
  }

  if (isAudioAd) {
    const audioAdsQueueLength = yield select(
      D.getInDomain('audioAds.utils.getAudioAdsQueueLength', null),
    );
    const audioType =
      audioAdsQueueLength > 1
        ? StatsConstants.TTMAudioTypes.AUDIO_AD
        : StatsConstants.TTMAudioTypes.MUSIC;

    yield put(StatsActions.ttmAudioAd(audioType));
  }

  // ttm action for when a song ends naturally
  if (reason === StatsConstants.TrackEndReasons.COMPLETE && isMusicTrack) {
    yield put(StatsActions.ttmNoUserAction());
  }

  if (!playlistType || playlistType === D.Constants.SourceTypes.SourceTypes.STATION) {
    return yield call(onTrackEndStation, reason, endedTrack);
  }

  if (reason === 'skipped') {
    const trackAnnotation = yield select(
      D.getInDomain('catalog.utils.getAnnotations'),
      endedTrack.get('pandoraId'),
    );
    if (D.getInDomain('catalog.utils.isCatalogVoiceTrack')(trackAnnotation)) {
      yield put(
        StatsActions.logVoiceTrackSkip({
          authorId: trackAnnotation.get('authorId'),
          messageId: endedTrack.get('pandoraId'),
          sourceId: playlistId,
        }),
      );
    }
  }
}

export function* onImplicitSkip({ data }) {
  const { statsReason, track } = data;
  const playlistType = yield select(D.getInDomain('playback.utils.getCurrentPlaylistType'));
  const playlistId = yield select(D.getInDomain('playback.utils.getCurrentPlaylist'));

  yield call(onTrackEnd, {
    reason: statsReason,
    playlistType,
    track,
    playlistId,
  });
}

export function* onTrackInterrupted({
  payload: { previousSourceId, previousType, previousTrack },
}) {
  yield call(onTrackEnd, {
    reason: StatsConstants.TrackEndReasons.STATION_CHANGED,
    track: previousTrack,
    playlistId: previousSourceId,
    playlistType: previousType,
  });
}

export function* onTrackEndError({ payload: { sourceId, token } }) {
  yield call(onTrackEnd, {
    reason: StatsConstants.TrackEndReasons.ERROR,
    playlistId: sourceId,
    playlistType: yield select(D.getInDomain('playback.utils.getPlaylistType'), sourceId),
    token,
  });
}

let _concertEventsSent = [];

export function* onLogConcertNotificationSent({ payload }) {
  // See if there's an event in concertEventsSent with the same eventId
  // If there is, return
  const event = _concertEventsSent.find(statsEvent => statsEvent.event_id === payload.eventId);
  if (event) {
    return;
  }
  const baseEventData = yield select(StatsUtils.getConcertEventData, payload);
  _concertEventsSent.push(baseEventData);

  const eventData = {
    ...baseEventData,
    action_type: StatsConstants.StatsActions.WEB_CONCERT_SENT,
  };

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.WEB_CONCERT_NOTIFICATION_ACTION,
      eventData,
    },
  ]);
}

export function* onLogConcertNotificationViewed({ payload }) {
  const baseEventData = yield select(StatsUtils.getConcertEventData, payload);
  const eventData = {
    ...baseEventData,
    action_type: StatsConstants.StatsActions.WEB_CONCERT_VIEWED,
  };

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.WEB_CONCERT_NOTIFICATION_ACTION,
      eventData,
    },
  ]);
}

// eslint-disable-next-line require-yield
export function* onLogConcertNoLongerOnPage({ payload }) {
  _concertEventsSent = _concertEventsSent.filter(statsEvent => {
    return statsEvent.event_id !== payload.eventId;
  });
}

export function* onLogConcertNotificationClicked({ payload }) {
  const baseEventData = yield select(StatsUtils.getConcertEventData, payload);
  const eventData = {
    ...baseEventData,
    action_type: StatsConstants.StatsActions.WEB_CONCERT_CLICKED,
  };

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.WEB_CONCERT_NOTIFICATION_ACTION,
      eventData,
    },
  ]);
}

export function* getPageView(path) {
  // This could use a good clean-up.
  let eventData;
  const isPartnerUpgradeFlow = yield select(D.Selectors.Payments.isPartnerUpgradeFlow);
  const isInSpecialOfferFlow = yield select(D.Selectors.Payments.isInSpecialOfferFlow);
  if (D.Utils.Route.isPathInMyStations(path)) {
    const sort = yield select(D.Utils.MyStationsUtils.getMyStationsDisplay);
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.MY_STATIONS,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]:
        sort === D.Constants.MyStations.ToggleValues.A_TO_Z ? 'alpha_sort' : 'recent_sort',
    };
  } else if (D.Utils.Route.isPathInNowPlayingPath(path)) {
    const isHistoryOpen = yield select(D.Utils.UIState.getIsSessionHistoryOpen);
    const isAnon = yield select(D.Utils.Listener.isAnonymousListener);
    const hasPremiumFeatures = yield select(D.Utils.Listener.hasPremiumFeatures);
    eventData = {
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: isAnon
        ? StatsConstants.ViewModes.ANONYMOUS
        : StatsConstants.ViewModes.STANDARD,
    };
    if (hasPremiumFeatures) {
      eventData[StatsConstants.DefaultFieldNames.PAGE_VIEW] = isHistoryOpen
        ? StatsPageViewNames.SESSION_HISTORY_PREMIUM
        : StatsPageViewNames.NOW_PLAYING_PREMIUM;
    } else {
      eventData[StatsConstants.DefaultFieldNames.PAGE_VIEW] = isHistoryOpen
        ? StatsPageViewNames.SESSION_HISTORY
        : StatsPageViewNames.NOW_PLAYING;
    }
  } else if (D.Utils.Route.isBackstageArtistPath(path)) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BACKSTAGE_ARTIST,
    };
  } else if (D.Utils.Route.isBackstageArtistFullBioPath(path)) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BACKSTAGE_ARTIST_FULL_BIO,
    };
  } else if (D.Utils.Route.isBackstageArtistAllSongsPath(path)) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BACKSTAGE_ARTIST_ALL_SONGS,
    };
  } else if (D.Utils.Route.isBackstageArtistAllAlbumsPath(path)) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BACKSTAGE_ARTIST_ALL_ALBUMS,
    };
  } else if (D.Utils.Route.isBackstageAlbumPath(path)) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BACKSTAGE_ALBUM,
    };
  } else if (D.Utils.Route.isBackstageAlbumDescriptionPath(path)) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BACKSTAGE_ALBUM_DESCRIPTION,
    };
  } else if (D.Utils.Route.isBackstageTrackPath(path)) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BACKSTAGE_TRACK,
    };
  } else if (D.Utils.Route.isBackstageTrackLyricsPath(path)) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BACKSTAGE_TRACK_LYRICS,
    };
  } else if (D.Utils.Route.isBackstageTrackCreditsPath(path)) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BACKSTAGE_TRACK_CREDITS,
    };
  } else if (D.Utils.Route.isBackstageTrackSimilarSongsPath(path)) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]:
        StatsPageViewNames.BACKSTAGE_TRACK_SIMILAR_SONGS,
    };
  } else if (D.Utils.Route.isBackstageGenrePath(path)) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BACKSTAGE_GENRE,
    };
  } else if (D.Utils.Route.isBrowsePath(path)) {
    eventData = StatsUtils.getBrowsePageView(path);
  } else if (D.Utils.Route.isPathInBackstagePodcast(path)) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: yield select(mapPathToPageView, path),
    };
  } else if (D.Utils.Route.getLoginPath() === path) {
    let pageView = yield select(D.getInDomain('listener.selectors.auth.isCaptchaRequired', false))
      ? StatsPageViewNames.LOGIN_CAPTCHA
      : StatsPageViewNames.LOGIN;
    let viewMode = StatsConstants.ViewModes.LOGIN_PAGE_SHOWN;
    if (isPartnerUpgradeFlow) {
      const partnerPrefix = yield select(StatsUtils.getPartnerNamePrefix);
      pageView = partnerPrefix + pageView;
      viewMode = StatsConstants.ViewModes.PARTNER_URL_LOGIN;
    } else if (isInSpecialOfferFlow) {
      viewMode = StatsConstants.ViewModes.OFFER_URL_LOGIN;
    }
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: pageView,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: viewMode,
    };
  } else if (D.Utils.Route.isRegisterPath(path)) {
    let pageView = StatsPageViewNames.REGISTRATION;
    let viewMode = StatsConstants.ViewModes.STANDARD;
    if (isPartnerUpgradeFlow) {
      const partnerPrefix = yield select(StatsUtils.getPartnerNamePrefix);
      pageView = partnerPrefix + pageView;
      viewMode = StatsConstants.ViewModes.PARTNER_URL_REGISTRATION;
    } else if (isInSpecialOfferFlow) {
      viewMode = StatsConstants.ViewModes.OFFER_URL_REGISTRATION;
    }
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: pageView,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: viewMode,
    };
  } else if (D.Utils.Route.getForgotPasswordPath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.FORGOT_PASSWORD,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.STANDARD,
    };
  } else if (D.Utils.Route.getResetPasswordPath() === path) {
    const pageView = yield select(D.getInDomain('listener.selectors.auth.isCaptchaRequired', false))
      ? StatsPageViewNames.RESET_PASSWORD_CAPTCHA
      : StatsPageViewNames.RESET_PASSWORD;
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: pageView,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.STANDARD,
    };
  } else if (D.Utils.Route.getInfoPath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.ACCOUNT_SETTINGS,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.STANDARD,
    };
  } else if (D.Utils.Route.getNotificationsPath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.NOTIFICATION_SETTINGS,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.STANDARD,
    };
  } else if (D.Utils.Route.getContentSettingsPath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.CONTENT_SETTINGS,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.STANDARD,
    };
  } else if (D.Utils.Route.getSubscriptionPath() === path) {
    let viewMode = StatsConstants.ViewModes.SUBSCRIPTION;
    const isPartnerBilled = yield select(D.Selectors.Payments.isPartnerBilled);
    const isSpecialOffer = yield select(D.Selectors.Payments.isActiveProductSpecialOffer);
    if (isPartnerBilled) {
      const partnerPrefix = yield select(StatsUtils.getPartnerNamePrefix);
      viewMode = partnerPrefix + viewMode;
    } else if (isSpecialOffer) {
      viewMode = StatsConstants.ViewModes.FLEX_MARKETING_SUBSCRIPTION;
    }
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.SUBSCRIPTION,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: viewMode,
    };
  } else if (D.Utils.Route.getPlusPaymentPath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.PAYMENT_PLUS,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.UPGRADE_PLUS,
    };
  } else if (D.Utils.Route.getPremiumPaymentPath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.PAYMENT_PREMIUM,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.UPGRADE_PREMIUM,
    };
  } else if (D.Utils.Route.getFamilyPlanPaymentPath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.PAYMENT_FAMILY,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.UPGRADE_FAMILY_PLAN,
    };
  } else if (D.Utils.Route.getPremiumStudentPlanPaymentPath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.PAYMENT_STUDENT,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.UPGRADE_STUDENT,
    };
  } else if (D.Utils.Route.getPremiumMilitaryPlanPaymentPath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.PAYMENT_MILITARY,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.UPGRADE_MILITARY,
    };
  } else if (D.Utils.Route.getPremiumStudentPlanVerificationPath() === path) {
    const isStudent = yield select(D.Selectors.Payments.isActiveProductDiscountTypeStudent);
    eventData = isStudent
      ? {
          [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.REVERIFICATION_STUDENT,
          [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.UPGRADE_STUDENT,
        }
      : {
          [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.VERIFICATION_STUDENT,
          [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.UPGRADE_STUDENT,
        };
  } else if (D.Utils.Route.getPremiumMilitaryPlanVerificationPath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.VERIFICATION_MILITARY,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.UPGRADE_MILITARY,
    };
  } else if (D.Utils.Route.getUpgradePath() === path) {
    const pageView = StatsPageViewNames.UPGRADE_LANDING;
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: pageView,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.STANDARD,
    };
  } else if (D.Utils.Route.getPlusUpgradePath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.UPGRADE_LANDING,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.UPGRADE_PLUS,
    };
  } else if (D.Utils.Route.getPremiumUpgradePath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.UPGRADE_LANDING,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.UPGRADE_PREMIUM,
    };
  } else if (D.Utils.Route.getFamilyPlanUpgradePath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.UPGRADE_LANDING,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.UPGRADE_FAMILY_PLAN,
    };
  } else if (D.Utils.Route.getPremiumStudentPlanUpgradePath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.UPGRADE_LANDING,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.UPGRADE_STUDENT,
    };
  } else if (D.Utils.Route.getPremiumMilitaryPlanUpgradePath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.UPGRADE_LANDING,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.UPGRADE_MILITARY,
    };
  } else if (D.Utils.Route.getPlansPath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.UPGRADE_LANDING,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.VIEW_PLANS,
    };
  } else if (D.Utils.Route.getRedeemPath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.REDEEM_CODE,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.STANDARD,
    };
  } else if (D.Utils.Route.getLandingPath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.LANDING_PAGE,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.STANDARD,
    };
  } else if (D.Utils.Route.isProfilePath(path)) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.PROFILE,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.STANDARD,
    };
  } else if (D.Utils.Route.getProfileThumbsPath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.PROFILE,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.THUMBS_UP,
    };
  } else if (D.Utils.Route.isStationDetailsPath(path)) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.STATION_DETAILS,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.STANDARD,
    };
  } else if (path.indexOf(D.Utils.Route.getRegistrationBlockedPath()) === 0) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.REGISTRATION,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.UNDER_13,
    };
  } else if (D.Utils.Route.isPlaylistPath(path)) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.PLAYLIST,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.PLAYLIST_TRACKS,
    };
  } else if (D.Utils.Route.isCollectionPath(path)) {
    const pageType = D.Utils.Route.getCollectionPageType(path);
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.COLLECTION,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: pageType,
    };
    const currentFilter = D.Utils.Route.getCollectionPageType(path);
    const isPodcastType = D.getInDomain(
      'collection.utils.isPodcastFilterType',
      false,
    )(currentFilter);
    const filteredCollectionHasItems = yield select(
      D.getInDomain('collection.utils.collectionHasFilteredTypeItems'),
      currentFilter,
    );
    if (isPodcastType && !filteredCollectionHasItems) {
      eventData = {
        [StatsConstants.DefaultFieldNames.VIEW_MODE]:
          pageType === FilterNavTypes.PODCASTS
            ? StatsConstants.PodcastViewModes.PROGRAM_VIEW_EMPTY
            : StatsConstants.PodcastViewModes.EPISODE_VIEW_EMPTY,
      };
    }
    if (isPodcastType && filteredCollectionHasItems) {
      eventData = {
        [StatsConstants.DefaultFieldNames.VIEW_MODE]:
          pageType === FilterNavTypes.PODCASTS
            ? StatsConstants.PodcastViewModes.PROGRAM_HAS_COLLECTED
            : StatsConstants.PodcastViewModes.EPISODE_HAS_COLLECTED,
      };
    }
  } else if (D.Utils.Route.getMobileRegisterWelcomePath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.REGISTRATION_SUCCESS,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]:
        StatsConstants.ViewModes.REGISTRATION_SUCCESS_PAGE_SHOWN,
    };
  } else if (D.Utils.Route.getMobileSignInWelcomePath() === path) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.LOGIN_SUCCESS,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]:
        StatsConstants.ViewModes.LOGIN_SUCCESS_PAGE_SHOWN,
    };
  } else if (D.Utils.Route.isInSearchPath(path)) {
    eventData = {
      [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.SEARCH,
      [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.STANDARD,
    };
  } else {
    return null;
  }
  return eventData;
}

export function* logLocationChange(action) {
  const path = action.payload.pathname;
  const eventData = yield call(getPageView, path);

  if (!eventData) return;

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_WEB_VIEW_MODE,
      eventData,
    },
  ]);
}

export function* onStationListHovered() {
  const isAnon = yield select(D.Utils.Listener.isAnonymousListener);
  if (!isAnon) {
    return;
  }

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_WEB_VIEW_MODE,
      eventData: {
        [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.ANONYMOUS_LISTENING,
        [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.HOVER,
      },
    },
  ]);
}

export function* onStationNarrativeDisplayed(action) {
  const isAnon = yield select(D.Utils.Listener.isAnonymousListener);
  if (!isAnon || action.data.doShow === false) {
    return;
  }

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_WEB_VIEW_MODE,
      eventData: {
        [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.ANONYMOUS_LISTENING,
        [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.STATION_HINT,
      },
    },
  ]);
}

export function* onSamplePlayback() {
  let pageView;
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  if (D.Utils.Route.isBackstageAlbumPath(currentPath)) {
    pageView = StatsPageViewNames.BACKSTAGE_ALBUM;
  } else if (D.Utils.Route.isBackstageTrackPath(currentPath)) {
    pageView = StatsPageViewNames.BACKSTAGE_TRACK;
  } else {
    return;
  }

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_WEB_VIEW_MODE,
      eventData: {
        [StatsConstants.DefaultFieldNames.PAGE_VIEW]: pageView,
        [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.SAMPLE,
      },
    },
  ]);
}

export function* onSearchUiUpdate(action) {
  const { hasFocus = false, searchActive = false } = action.data;
  if (hasFocus && searchActive) {
    yield D.tryCall(logEvents, [
      {
        eventName: StatsConstants.Events.EVENT_WEB_VIEW_MODE,
        eventData: {
          [StatsConstants.DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.SEARCH,
          [StatsConstants.DefaultFieldNames.VIEW_MODE]: StatsConstants.ViewModes.STANDARD,
        },
      },
    ]);
  }
}

export function* onOpenSignupLoginModal() {
  const timedOut = yield select(D.Utils.Listener.isAnonTimedOut);
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);

  const viewMode = timedOut
    ? StatsConstants.ViewModes.ANONYMOUS_LISTENING_TIMEOUT_REGISTRATION
    : StatsConstants.ViewModes.ANONYMOUS;
  const eventData = {
    [StatsConstants.DefaultFieldNames.PAGE_VIEW]: currentPath,
    [StatsConstants.DefaultFieldNames.VIEW_MODE]: viewMode,
  };

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_WEB_VIEW_MODE,
      eventData,
    },
  ]);
}

export function* onAudioTrackPlayback({ payload }) {
  const currentTrack = yield select(
    D.getInDomain('playback.utils.getCurrentTrackFromActivePlaylist'),
  );
  const browserInfo = yield select(D.Utils.ClientInfo.getSimplifiedDeviceOS);
  const listenerId = yield select(D.Utils.Listener.getCurrentListenerIdFromState);
  const audioURL = currentTrack ? currentTrack.get('audioURL') : null;
  const dataToAdd = {
    audio_url: audioURL,
    browser_id: browserInfo,
    day: Date.now().toString(),
    listener_id: +listenerId,
    test_group: StatsConstants.AUDIO_STATS_TEST_GROUP_NAME,
  };
  const dataToSend = Object.assign({}, payload, dataToAdd);

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.AUDIO_TRACK_PLAYBACK,
      eventData: dataToSend,
    },
  ]);
}

export function* logFPSData(frames_per_second) {
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const sessionLength = yield select(D.Utils.Listener.getListenerSessionLength);
  const pageViewData = yield call(getPageView, currentPath);
  const listenerSessionToken = yield select(D.Utils.Listener.getListenerSessionToken);
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.MEASURE_FRAMES,
      eventData: {
        frames_per_second,
        session_length: `${sessionLength}`,
        listener_session_token: listenerSessionToken,
        ...pageViewData,
      },
    },
  ]);
}

function getChronosLifeCycleSubTypeFromActionType(actionType) {
  return StatsConstants.mapActionTypeToEventSubType[actionType];
}

function* logGetAdlistTimeout({
  currentTrackSecondsPlayed,
  currentTrackStationId,
  currentTrackToken,
  currentTrackType,
  nextTrackStationId,
  secondsSinceDisplayAd,
  secondsUntilAd,
  userInitiatedTrackEnd,
}) {
  const requestString = JSON.stringify({
    currentStationId: currentTrackStationId,
    nextStationId: nextTrackStationId,
    secondsPlayed: currentTrackSecondsPlayed,
    secondsSinceDisplayAd,
    secondsUntilAd,
    testMode: select(D.Utils.TunerVars.getTestMode),
    trackToken: currentTrackToken,
    trackType: currentTrackType,
    userInitTrackEnd: userInitiatedTrackEnd,
    videoAdReady: D.Utils.PageVisibility.isHidden() ? false : true,
  });

  const eventData = {
    accessory_id: null,
    ad_id: null,
    ad_unit_id: null,
    bluetooth_device_name: null,
    correlation_id: null,
    creative_id: null,
    device_model: null,
    event_description: null,
    event_sub_type: null,
    event_type: 'timeout',
    is_pandora_link: false,
    line_id: null,
    request_string: requestString,
  };

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.StatsActions.CHRONOS_LIFECYCLE,
      eventData,
    },
  ]);
}

export function* onGetAdlistFail({ request, error }) {
  if (D.Utils.Network.isNetworkTimeoutError(error) && request) {
    yield D.tryCall(logGetAdlistTimeout, request.content);
  }
}

function* monitorStoreSize() {
  while (LOOP) {
    yield delay(LOCAL_STORE_SIZE_LOGGING_INTERVAL);
    const size = yield select(D.getStoreSize);
    const persistedSize = yield call(D.getPersistedStoreSize);
    yield D.tryCall(logLocalStoreSize, size, persistedSize);
  }
}

const NUMBER_OF_FRAMES_TO_SAMPLE = 10;
// Sample every 10 minutes max
const MAX_SAMPLING_FREQUENCY = 1000 * 60 * 10;
let hasSingleFPSMonitorStarted = false;

export function* measureFPS() {
  let start = D.Utils.Time.perfNow();
  let frames = 0;
  if (hasSingleFPSMonitorStarted) return;
  hasSingleFPSMonitorStarted = true;

  function loopForFixedFrames(resolve) {
    requestAnimationFrame(() => {
      frames++;
      if (frames < NUMBER_OF_FRAMES_TO_SAMPLE) {
        loopForFixedFrames(resolve);
      } else {
        resolve(D.Utils.Time.perfNow());
      }
    });
  }

  function loopFrames() {
    frames = 0;
    return new Promise(res => {
      start = D.Utils.Time.perfNow();
      loopForFixedFrames(res);
    });
  }

  while (LOOP) {
    const waitTime = Math.round(MAX_SAMPLING_FREQUENCY * Math.random());
    yield delay(waitTime);
    if (D.Utils.PageVisibility.isHidden()) {
      continue;
    }
    const finish = yield call(loopFrames);
    const fps = Math.min(60, (frames / (finish - start)) * 1000).toFixed(1);
    yield call(logFPSData, fps);
  }
}

export function* logInboundLinkEvent(
  sourcePartnerId,
  sourceCorrelationId,
  state,
  isStartupOfInboundTrackingEvent,
  action,
) {
  const listenerSessionToken = yield select(D.Utils.Listener.getListenerSessionToken);
  const path = getIn(state, 'routing.locationBeforeTransitions.pathname');
  const isBackstageGenrePath = D.Utils.Route.isBackstageGenrePath(path);
  const query = getIn(state, 'routing.locationBeforeTransitions.search');
  const actionType = action && action.type;
  const actionData = action && action.data;
  const response = action && action.response;
  const shareConfig = action && actionData.overlayConfig;
  const pandoraIdFromNowPlayingUrl = D.Utils.Route.getPandoraIdFromNowPlayingSourceURL(path);
  const pandoraIdFromSourceUrl = yield select(D.Utils.Route.getPandoraIdFromSourceURL, path);
  const pandoraIdFromStore = yield select(StatsUtils.getPandoraIdFromStartupQueryParams);
  let sourceType;
  let sourceId;
  let stationCode;
  let sourcePandoraId;
  let stationId;
  let createStationParams;

  if (actionData) {
    createStationParams = actionData.createStationParams;
    stationId = actionData.id || actionData.stationId;
    const station =
      D.getInDomain('playback.utils.getStationByStationId')(state, pandoraIdFromNowPlayingUrl) ||
      D.getInDomain('playback.utils.getStationByStationId')(state, stationId);

    if (createStationParams) {
      // Sets source to `STATION`, only if its a station or a genre page
      if (D.Utils.Route.isStationBasePath(path) || isBackstageGenrePath) {
        sourceType = createStationParams.type;
      }

      sourceId = createStationParams.musicId
        ? createStationParams.musicId
        : createStationParams.sourceId;
      stationCode = createStationParams.stationCode;
    }

    if (isBackstageGenrePath) {
      sourceId = D.Utils.Route.getPandoraIdFromGenreURL(state, stationId) || sourceId;
      // Set store pandoraId only if genre station is started/clicked
      sourcePandoraId = pandoraIdFromStore
        ? pandoraIdFromStore
        : D.Utils.Route.isStationBasePath(path) && sourceId;
    } else {
      // If the user is anonymous, update the sourceId on register
      if (
        (station && D.getInDomain('playback.utils.getIsShared')(station)) ||
        D.Utils.Listener.isAnonymousListener(state)
      ) {
        // sourceId should be updated only if the source StationId for shared stations is clicked
        const isSourceClicked = stationCode
          ? stationCode.indexOf(pandoraIdFromNowPlayingUrl) !== -1
          : false;
        sourceId = isSourceClicked || stationId === pandoraIdFromStore ? stationId : null;
        sourcePandoraId = sourceId;
      } else {
        /*
        Set store pandoraId only if source station (e.g. station/12345678) is started/clicked and
        not when set a station is started from tuner bar
        */
        if (!sourceId && stationId === pandoraIdFromNowPlayingUrl) {
          sourceId = stationId;
        }

        const getSourceId = sourceId === pandoraIdFromNowPlayingUrl ? sourceId : null;
        sourcePandoraId = sourceType
          ? getSourceId
          : pandoraIdFromSourceUrl || pandoraIdFromNowPlayingUrl;
      }
    }
  }

  // Set's pandoraId for genre station
  if (response) {
    const genres = getIn(response, 'entities.genres');
    sourcePandoraId = genres[Object.keys(genres)[0]].pandoraId;
  }

  let actionName;
  switch (actionType) {
    case D.Constants.Playback.ActionTypes.SET_PLAYLIST: {
      const { isSimStreamViolation } = actionData;
      actionName = isSimStreamViolation
        ? StatsConstants.StatsActions.SIM_STREAM_PLAY
        : StatsConstants.Events.SOURCE_PLAY;
      if (actionData && !isBackstageGenrePath) {
        // Sets pandoraId only if the source is clicked and not when tuner starts playing previous track
        if (
          pandoraIdFromNowPlayingUrl === actionData.id ||
          pandoraIdFromSourceUrl === actionData.id
        ) {
          sourceId = actionData.id;
          sourcePandoraId = pandoraIdFromStore ? pandoraIdFromStore : sourceId;
        }
      }
      break;
    }

    case D.Constants.Overlay.default.CHECK_AND_MAYBE_OPEN_OVERLAY: {
      const hasShareConfigId = shareConfig && shareConfig.ID;
      if (
        hasShareConfigId &&
        shareConfig.ID.indexOf(D.Constants.Overlay.OVERLAY_SUBTYPES.MODALSHARE) !== -1
      ) {
        sourceId = shareConfig.PROPS.trackId || shareConfig.PROPS.parentSourceId;
        const genrePandoraId = D.Utils.Route.getPandoraIdFromGenreURL(state, sourceId);
        const isGenreStation =
          genrePandoraId && D.getInDomain('playback.utils.isGenrePandoraId')(genrePandoraId);
        const shareStation =
          D.getInDomain('playback.utils.getStationByStationId')(
            state,
            pandoraIdFromNowPlayingUrl,
          ) || D.getInDomain('playback.utils.getStationByStationId')(state, sourceId);
        if (isGenreStation) {
          const genreStationId = D.Utils.Route.getPandoraIdFromGenreURL(state, sourceId, true);
          sourceId = genreStationId === pandoraIdFromStore ? genreStationId : genrePandoraId;
        }
        sourcePandoraId = pandoraIdFromStore ? pandoraIdFromStore : sourceId;

        // Set pandoraID to a generated stationID for a shared station
        if (shareStation && D.getInDomain('playback.utils.getIsShared')(shareStation)) {
          sourcePandoraId = pandoraIdFromStore ? pandoraIdFromStore : sourceId;
        }
      } else {
        return;
      }
      actionName = StatsConstants.Events.SOURCE_SHARED;
      break;
    }
    case D.Constants.Analytics.ActionTypes.STATION_STARTED:
      if (actionData) {
        if (isEmpty(createStationParams)) {
          // Sets sourceId for station started from an artist/song
          sourceId = actionData.pandoraId || actionData.stationId;
        }
      } else {
        return;
      }
      actionName = StatsConstants.Events.SOURCE_STATION_STARTED;
      break;

    case D.Constants.Analytics.ActionTypes.ADD_COLLECTION_ITEM:
      if (actionData) {
        const dataId = actionData.pandoraId;
        stationId = pandoraIdFromStore ? pandoraIdFromStore : pandoraIdFromNowPlayingUrl;
        const stationIdIndex = dataId && dataId.indexOf(stationId);
        // Get stationId if station is user created (ST:0:25956810948230211 -> 25956810948230211)
        const getUserCreatedStationId = stationIdIndex !== -1 && dataId.slice(stationIdIndex);
        sourceId = dataId.startsWith(D.Constants.SourceTypes.PandoraTypes.USER_STATION)
          ? getUserCreatedStationId
          : dataId;
        sourcePandoraId = pandoraIdFromStore ? pandoraIdFromStore : sourceId;
      }
      actionName = StatsConstants.Events.SOURCE_ADDED_TO_MY_MUSIC;
      break;

    case D.Constants.Api.ApiActions.FETCH_GENRES_BY_CATEGORY_ID_SUCCESS:
      actionName = '';
      break;

    case D.Constants.Analytics.ActionTypes.ENTER_REDEEM_FLOW_FOR_URL:
      actionName = StatsConstants.Events.SOURCE_CLICKED;
      break;

    default:
      actionName = isStartupOfInboundTrackingEvent ? StatsConstants.Events.SOURCE_CLICKED : '';
      break;
  }

  const newParams = {
    part: sourcePartnerId,
    corr: sourceCorrelationId,
    pandoraId: sourcePandoraId,
    rawUrl: D.Utils.Route.constructFullUrl(path + query),
  };

  const originalParams = (yield select(D.Utils.Route.getQueries)).toJS();
  const params = { ...originalParams, ...newParams };
  yield call(D.putAndWait, D.Actions.Application.setStartupQueryParameters(params));

  const { rawUrl, pandoraId, partnerId, correlationId } = yield select(
    StatsUtils.getInboundTrackingDataFromState,
  );

  const data = {
    action: actionName,
    is_pandora_link: false,
    link_correlation_id: correlationId,
    link_partner_id: partnerId,
    pandora_session_id: listenerSessionToken,
    raw_url: rawUrl,
    source_pandora_id: pandoraId,
  };

  /*
  Log a default `Clicked` event when the user lands on an inbound link or
  log an event if the user interacts with the source pandoraId
  */
  if (
    (pandoraId && pandoraId === sourceId) ||
    actionName === StatsConstants.Events.SOURCE_CLICKED
  ) {
    yield D.tryCall(logEvents, [
      {
        eventName: StatsConstants.Events.EVENT_PARTNER_LINK_ACTIONS,
        eventData: data,
      },
    ]);
  }
}

export function* onOpenSessionHistory() {
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const eventData = yield call(getPageView, currentPath);
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_WEB_VIEW_MODE,
      eventData,
    },
  ]);
}

export function* onOpenShareModal({ payload: { sourceId, parentSource } }) {
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const pageViewData = (yield call(getPageView, currentPath)) || {};

  const actionName = StatsConstants.StatsActions.VIEW;
  const listenerId = yield select(D.Utils.Listener.getCurrentListenerIdFromState);
  const shareId = sourceId + '-' + listenerId;

  let shareObject = D.Utils.SourceType.getSourceTypeFromPandoraId(sourceId) || parentSource;

  if (shareObject === D.Constants.SourceTypes.PandoraTypes.STATION) {
    shareObject = D.Constants.SourceTypes.SourceTypes.STATION;
  }

  shareObject = shareObject.toLowerCase();

  const data = {
    action: actionName,
    listener_id: `${listenerId}`,
    options_count: '3',
    options_map: 'facebook,twitter,copy_link',
    share_id: shareId,
    share_object: shareObject,
    source: pageViewData.page_view,
    source_id: sourceId,
  };
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_SHARE,
      eventData: data,
    },
  ]);
}

export function* onShareOptionClicked({ payload }) {
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const pageViewData = (yield call(getPageView, currentPath)) || {};

  const listenerId = yield select(D.Utils.Listener.getCurrentListenerIdFromState);
  const shareId = payload.sourceId + '-' + listenerId;

  let shareObject =
    D.Utils.SourceType.getSourceTypeFromPandoraId(payload.sourceId) ||
    D.Constants.SourceTypes.PandoraTypes.STATION;

  if (shareObject === D.Constants.SourceTypes.PandoraTypes.STATION) {
    shareObject = D.Constants.SourceTypes.SourceTypes.STATION;
  }

  shareObject = shareObject.toLowerCase();

  const data = {
    action: StatsConstants.StatsActions.SHARE,
    email: 'false',
    facebook: payload.facebook.toString(),
    pandora: 'false',
    share_id: shareId,
    share_method: payload.shareMethod,
    share_object: shareObject,
    source: pageViewData.page_view,
    source_id: payload.sourceId,
    twitter: payload.twitter.toString(),
    twitter_handle: payload.twitterHandle,
  };
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_SHARE,
      eventData: data,
    },
  ]);
}

export function* onCloseSessionHistory({ payload }) {
  if (payload.isLeavingNowPlaying) {
    return;
  }

  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const eventData = yield call(getPageView, currentPath);
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_WEB_VIEW_MODE,
      eventData,
    },
  ]);
}

export function* logAbExperimentDiversionPointEvent({ payload: { experimentId, treatmentId } }) {
  yield call(logAbTreatmentArmExposureEvent, experimentId, treatmentId);
}

export function* logAbTreatmentArmExposureEvent(experimentId, treatmentId) {
  const now = D.Utils.Time.getNow();
  const nowDate = D.Utils.Time.formatMillisToDashDate(now);
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.AB_AUDIENCE_LOG,
      eventData: {
        [StatsConstants.DefaultFieldNames.CLIENT_CONTEXT]: window.location.href,
        [StatsConstants.DefaultFieldNames.EXPERIMENT_STATUS]: 'RUNNING',
        [StatsConstants.DefaultFieldNames.KEY_AB_EXPERIMENT_ID]: experimentId,
        [StatsConstants.DefaultFieldNames.LOG_DATE]: nowDate,
        [StatsConstants.DefaultFieldNames.SOURCE]: 'WEB',
        [StatsConstants.DefaultFieldNames.TIME]: now,
        [StatsConstants.DefaultFieldNames.RESPONSE_TREATMENT_ID]: treatmentId,
      },
    },
  ]);
}

export function* logAdBlockerDetected({ payload }) {
  const isAdBlockerDetected = yield select(
    D.getInDomain('application.utils.isAdBlockerDetected', false),
  );
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.WEB_AD_BLOCKER,
      eventData: {
        ad_blocker_detected_source: payload.source,
        ad_blocker_detected: isAdBlockerDetected.toString(),
      },
    },
  ]);
}

export function* logAdBlockerHammerModalEvent({ payload: { reason, clicked = true } }) {
  const adBlockCoachmarkReason = reason || null;
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.COACHMARK,
      eventData: {
        coachmark_id: StatsExternals.CoachmarkIds.AD_BLOCKER_DETECTED_T1_MODAL,
        coachmark_type: StatsConstants.CoachmarkTypes.AD_BLOCKER,
        reason: adBlockCoachmarkReason,
        clicked,
      },
    },
  ]);
}

export function* logAdBlockerModalEvent({ payload }) {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.COACHMARK,
      eventData: {
        coachmark_id: `${StatsExternals.CoachmarkIds.AD_BLOCKER_DETECTED}_${payload.source}`,
        coachmark_type: StatsConstants.CoachmarkTypes.AD_BLOCKER,
      },
    },
  ]);
}

const audioUpdate =
  percent =>
  ({ type, payload }) => {
    if (type === D.Constants.AudioPlayer.AudioPlayerActions.UPDATE) {
      return (
        percent <=
        (payload[D.Constants.AudioPlayer.AudioPlayerKeys.CURRENT_TIME] /
          payload[D.Constants.AudioPlayer.AudioPlayerKeys.DURATION]) *
          100
      );
    }
    return false;
  };

const sendAudioLifecycleEventFactory = (audioToken, audioUrl) =>
  function* sendAudioLifecycleEvent(lifecyclePoint) {
    yield D.tryCall(logEvents, [
      {
        eventName: StatsConstants.Events.AUDIO_PLAYBACK_LIFECYCLE,
        eventData: {
          audio_token: audioToken,
          audio_url: audioUrl,
          audio_lifecycle_point: lifecyclePoint,
        },
      },
    ]);
  };

function* logAudioLifecycleEvents(audioToken, audioUrl, initialLifecyclePoint = 'started') {
  const sendAudioEvent = sendAudioLifecycleEventFactory(audioToken, audioUrl);

  yield sendAudioEvent(initialLifecyclePoint);

  yield take(audioUpdate(25));
  yield sendAudioEvent('25%');

  yield take(audioUpdate(50));
  yield sendAudioEvent('50%');

  yield take(audioUpdate(75));
  yield sendAudioEvent('75%');
}

export function* handleAudioLifecycleEventsOnAppLaunch({ payload: { token, url } }) {
  D.Utils.Time.recordTimeAtMoment(D.Constants.App.TimingMoments.APP_LAUNCHED_TTM);
  const handler = yield fork(
    logAudioLifecycleEvents,
    token,
    url,
    StatsConstants.TTMActions.APP_LAUNCHED,
  );
  yield take(D.Constants.Integrations.ActionTypes.TRACK_STARTED_FROM_SOURCE);
  yield cancel(handler);
}

export function* handleAudioLifecycleEvents({ payload: { track } }) {
  yield call(logAudioLifecycleEvents, track.get('trackToken'), track.get('audioURL'));
}

export function* logTimeToMusicEvent(timeToMusic, ttmAction, audioType) {
  const event = StatsUtils.createTimeToMusicStatsObject(timeToMusic, ttmAction, audioType);
  yield call(logTimeToMusicEvents, [event]);
}

export function* logTimeToMusicEvents(events) {
  const defaultEventData = yield select(StatsUtils.getDefaultEventData);
  const defaultTTMEventData = StatsUtils.createTimeToMusicDefaultEventData(defaultEventData);
  yield put(StatsActions.addEvents(events, defaultTTMEventData));
}

export function* logBufferingEvents(events) {
  yield D.tryCall(logEvents, events, StatsUtils.getBufferingDefaultEventData);
}

// We want to capture time to music of known user who has stations and app autologs them in and autoplays music
// We want to avoid scenarios of known user who has to login, or anonymous user who has to create station
export function* recordInitialTTMHandler(action) {
  // We only want to log TTM for users who auto authenticate
  const isAutoStartEnabled = yield select(D.getInDomain('playback.utils.isAutoStartEnabled'));
  const isUserAuth = yield select(D.Utils.Listener.wasAuthUserInitiated);
  const isAdBlockerDetected = yield select(D.getInDomain('application.utils.isAdBlockerDetected'));

  if (isUserAuth || isAdBlockerDetected || !isAutoStartEnabled) {
    return;
  }
  // We only want to register for an autologged in user
  const stationCount = action.response.result.totalStations;
  // If 0, we're not autoplaying music and would throw off time to music metric
  // by including time to type login or create station
  // TODO: In future, getStations might be done in server; can't rely on this if so
  if (stationCount === 0) {
    return;
  }
  yield take(D.Constants.AudioPlayer.AudioPlayerActions.PLAYING);
  const loadTime = yield call(D.Utils.Performance.markPerformanceTime, 'First Playback');
  yield logTimeToMusicEvent(loadTime, StatsConstants.TTMActions.APP_LAUNCHED);
}

export function* recordInitialTTM(action) {
  const handler = yield fork(recordInitialTTMHandler, action);
  yield take([
    D.Utils.AudioPlayerUtils.browserAutoplayBlockedPattern,
    D.Utils.AudioPlayerUtils.trackEndErrorUnrecoverablePattern,
    D.Constants.UseCases.ActionTypes.PAUSE_TRACK,
    D.Constants.UseCases.ActionTypes.PLAY_REWARDS_AD,
    D.Constants.UseCases.ActionTypes.PROMPT_STREAM_VIOLATION,
    D.Constants.UseCases.ActionTypes.REPLAY_TRACK,
    D.Constants.UseCases.ActionTypes.SCRUB_TRACK,
    D.Constants.UseCases.ActionTypes.SKIP_TRACK_BACK,
    D.Constants.UseCases.ActionTypes.SKIP_TRACK_FORWARD,
    D.Constants.UseCases.ActionTypes.SKIP_TRACK_FORWARD_CANCEL,
  ]);
  yield cancel(handler);
}

// ------------------------------------------------- STATS WORKERS -------------------------------------------------

export function* initializeLinkTracker() {
  const state = D.getStore().getState();
  const { partnerId, correlationId } = yield select(StatsUtils.getInboundTrackingDataFromState);

  if (partnerId && correlationId) {
    yield fork(logInboundLinkEvent, partnerId, correlationId, state, true);
  }
}

export function* logAudioTrackPlay({ payload: { playlistId, track } }) {
  if (playlistId) {
    const trackAnnotation = yield select(
      D.getInDomain('catalog.utils.getAnnotations'),
      track.get('pandoraId'),
    );
    if (D.getInDomain('catalog.utils.isCatalogVoiceTrack')(trackAnnotation)) {
      yield put(
        StatsActions.logVoiceTrackAudioImpression({
          authorId: trackAnnotation.get('authorId'),
          messageId: track.get('pandoraId'),
          sourceId: playlistId,
        }),
      );
    }
  }
}

export function* logVoiceTrackMetric({ payload: { authorId, messageId, sourceId, type } }) {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.VOICE_TRACK_METRIC,
      eventData: {
        [StatsConstants.VoiceTrackMetricKeys.AUTHOR_ID]: authorId,
        [StatsConstants.VoiceTrackMetricKeys.MESSAGE_ID]: messageId,
        [StatsConstants.VoiceTrackMetricKeys.SOURCE_ID]: sourceId,
        [StatsConstants.VoiceTrackMetricKeys.METRIC_TYPE]: type,
      },
    },
  ]);
}

export function* logRecentlyPlayedCarouselEvent(action) {
  if (!action.data) {
    // TODO-PAYLOAD: WEBCA-2514
    action.data = action.payload;
  }

  const { tileNum, componentEventType, pandoraId, destinationUrl, contentSection } = action.data;

  // If this is a SOURCE_CARD action, make sure the content section Recently Played
  if (
    D.Constants.SourceCard.ActionTypes[action.type] &&
    contentSection !== D.Constants.ContentSections.ContentSections.RECENTLY_PLAYED_CAROUSEL
  ) {
    return;
  }

  const eventType = componentEventType || action.payload.optionName;
  const currentFilter = yield select(D.getInDomain('collection.utils.getCurrentFilter', null));
  const actionType = destinationUrl
    ? StatsConstants.StatsActions.ROUTING
    : StatsConstants.mapActionTypeToStatsActionType[eventType];
  const viewMode = StatsConstants.mapFilterTypesToViewModes[currentFilter];

  if (viewMode && actionType) {
    yield D.tryCall(logEvents, [
      {
        eventName: StatsConstants.Events.EVENT_RECENTLY_PLAYED_CAROUSEL,
        eventData: {
          pandora_id: pandoraId,
          tile_num: tileNum,
          view_mode: viewMode,
          action: actionType,
        },
      },
    ]);
  }
}

export function* logDiscoveryTunerAction({
  payload: { stationId, currentModeId, modeId, modeIndex, currentTrackToken, musicId },
}) {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.LOG_DISCOVERY_TUNER_ACTION,
      eventData: {
        [StatsConstants.DiscoveryTunerActionKeys.STATION_ID]: stationId,
        [StatsConstants.DiscoveryTunerActionKeys.SELECTED_MODE_ID]: modeId,
        [StatsConstants.DiscoveryTunerActionKeys.INDEX]: modeIndex,
        [StatsConstants.DiscoveryTunerActionKeys.CURRENT_MODE_ID]: currentModeId,
        [StatsConstants.DiscoveryTunerActionKeys.SONG_ID]: musicId,
        [StatsConstants.DiscoveryTunerActionKeys.AUDIO_TOKEN]: currentTrackToken,
      },
    },
  ]);
}

export function* logPageRendered({ payload: { event, eventName } }) {
  const isCommunitySSO = yield select(D.Utils.Application.shouldNavigateToCommunity);
  const source = isCommunitySSO ? 'community_sso' : '';
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const pageViewData = yield call(getPageView, currentPath);
  const eventData = { source, action: event, ...pageViewData };

  yield D.tryCall(logEvents, [
    {
      eventName: eventName || StatsConstants.Events.WEB_REGISTRATION,
      eventData,
    },
  ]);
}

export function* logLinkClicked({ payload: { event } }) {
  const isCommunitySSO = yield select(D.Utils.Application.shouldNavigateToCommunity);
  const eventData = isCommunitySSO ? { source: 'community_sso' } : {};
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.WEB_REGISTRATION,
      eventData: {
        ...eventData,
        action: event,
      },
    },
  ]);
}

export function* logDiscoveryTunerScrolled({ payload: { event } }) {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.DISCOVERY_TUNER_SCROLL,
      eventData: {
        station_id: event.stationId,
        song_id: event.songId,
        audio_token: event.audioToken,
        current_mode_id: event.currentModeId,
      },
    },
  ]);
}

export function* logAPIErrorMessage({ payload: { messageName } }) {
  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.USER_FACING_MESSAGES,
      eventData: {
        message_name: messageName,
        message_type: StatsConstants.UserMessageTypes.INLINE,
        message_id: StatsConstants.mapMessageNameToId[messageName],
      },
    },
  ]);
}

export function* logToastErrorMessage({ payload: { messageName } }) {
  if (messageName) {
    yield D.tryCall(logEvents, [
      {
        eventName: StatsConstants.Events.USER_FACING_MESSAGES,
        eventData: {
          message_name: messageName,
          message_type: StatsConstants.UserMessageTypes.TOAST,
          message_id: StatsConstants.mapMessageNameToId[messageName],
        },
      },
    ]);
  }
}

export function* logStationReady({ data }) {
  if (data.newStation) {
    yield put(StatsActions.logStationCreated(data));
  }
  // log station being changed
  yield put(StatsActions.logStationStarted(data));
}

export function* onAddRemoveThumb(action) {
  /*
  APS sources. Non-APS sources are handled differently.
  */
  const isPositive = `${getIn(action, 'params.isPositive', null)}`;
  const sourceId = getIn(action, 'params.content.sourceId', null);
  const isAutoPlay = [
    D.Constants.SourceTypes.PandoraTypes.AUTOPLAY,
    D.Constants.SourceTypes.PandoraTypes.PODCAST_AUTOPLAY,
  ].includes(D.Utils.SourceType.getPandoraTypeFromPandoraId(sourceId));
  let eventName;
  let dataToSend;

  switch (action.type) {
    case D.Constants.Api.ApiActions.ACTION_THUMB_UP_SUCCESS:
    case D.Constants.Api.ApiActions.ACTION_THUMB_DOWN_SUCCESS:
      eventName = isAutoPlay
        ? StatsConstants.Events.ADD_AUTOPLAY_FEEDBACK
        : StatsConstants.Events.ADD_FEEDBACK;
      break;
    case D.Constants.Api.ApiActions.ACTION_REMOVE_THUMB_SUCCESS:
      eventName = isAutoPlay
        ? StatsConstants.Events.REMOVE_AUTOPLAY_FEEDBACK
        : StatsConstants.Events.DELETE_FEEDBACK;
      break;
  }

  if (isAutoPlay) {
    const trackPandoraId = getIn(action, 'params.content.trackToken', null);
    const trackTime = yield select(D.getInDomain('playback.utils.getPlaylistTrackTime'), sourceId);

    dataToSend = {
      autoplay_id: sourceId,
      is_positive: isPositive,
      track_pandora_id: trackPandoraId,
      track_time: Math.round(trackTime),
    };
  } else {
    const listenerState = yield select(D.Utils.Listener.getBranding);

    dataToSend = {
      is_positive: isPositive,
      listener_state: listenerState,
      music_id: sourceId,
    };
  }

  return yield D.tryCall(logEvents, [
    {
      eventName,
      eventData: dataToSend,
    },
  ]);
}

export function* onAddDeleteFeedback(action) {
  const isPositive = getIn(action, 'response.isPositive', null);
  const feedbackId = getIn(action, 'response.feedbackId', null);
  const stationId = getIn(action, 'response.stationId', null);
  let eventName;

  if (action.type === D.Constants.Api.ApiActions.ADD_FEEDBACK_SUCCESS) {
    eventName = StatsConstants.Events.ADD_FEEDBACK;
  } else if (action.type === D.Constants.Api.ApiActions.DELETE_FEEDBACK_SUCCESS) {
    eventName = StatsConstants.Events.DELETE_FEEDBACK;
  }

  return yield D.tryCall(logEvents, [
    {
      eventName,
      eventData: {
        is_positive: `${isPositive}`,
        feedback_id: +feedbackId,
        listener_state: null,
        station_id: +stationId,
      },
    },
  ]);
}

export function* onSetSourceRepeatMode({ payload }) {
  // To be grouped with audio playback events
  const RepeatModes = D.getInDomain('playback.constants.REPEAT_MODES');
  let action;

  switch (payload.mode) {
    case RepeatModes.NONE:
      action = StatsConstants.StatsActions.REPEAT_DISABLED;
      break;
    case RepeatModes.ALL:
      action = StatsConstants.StatsActions.REPEAT_SOURCE;
      break;
    case RepeatModes.ONE:
      action = StatsConstants.StatsActions.REPEAT_TRACK;
      break;
  }

  const params = {
    action,
    source: StatsConstants.StatsSources.WEB_PLAYBACK_CONTROLS,
    userInitiated: true,
  };
  yield call(logPlaybackInteractions, params);
}

export function* onSkipTrackForward() {
  // To be grouped with audio playback events
  const params = {
    action: StatsConstants.StatsActions.SKIP_FORWARD,
    source: StatsConstants.StatsSources.WEB_PLAYBACK_CONTROLS,
    userInitiated: true,
  };
  yield call(logPlaybackInteractions, params);
}

export function* onSkipTrackBack() {
  // To be grouped with audio playback events
  const params = {
    action: StatsConstants.StatsActions.SKIP_BACK,
    source: StatsConstants.StatsSources.WEB_PLAYBACK_CONTROLS,
    userInitiated: true,
  };
  yield call(logPlaybackInteractions, params);
}

export function* onSetSourceShuffleMode({ payload }) {
  // To be grouped with audio playback events
  const action =
    payload.enabled === true
      ? StatsConstants.StatsActions.SHUFFLE_ON
      : StatsConstants.StatsActions.SHUFFLE_OFF;
  const params = {
    action,
    source: StatsConstants.StatsSources.WEB_PLAYBACK_CONTROLS,
    userInitiated: true,
  };
  yield call(logPlaybackInteractions, params);
}

export function* onScrubTrack({ payload }) {
  // To be grouped with audio playback events
  let action;

  switch (payload.relativeValue) {
    case 15:
      action = StatsConstants.StatsActions.FAST_FORWARD_SEEK;
      break;
    case -15:
      action = StatsConstants.StatsActions.REWIND_SEEK;
      break;
    default:
      action = StatsConstants.StatsActions.SCRUB;
  }

  const params = {
    action,
    source: StatsConstants.StatsSources.WEB_PLAYBACK_CONTROLS,
    userInitiated: true,
  };
  yield call(logPlaybackInteractions, params);
}

export function* onIntentPlayTrack({ data }) {
  const { logPlaybackActionsAnalytics, playbackActionsAnalyticsUserInitiated } = data;
  if (!logPlaybackActionsAnalytics) {
    return;
  }

  const isSimStreamViolation = yield select(
    D.getInDomain('playback.utils.isStreamViolationErrorValid'),
  );
  const action = isSimStreamViolation
    ? StatsConstants.StatsActions.SIM_STREAM_PLAY
    : StatsConstants.StatsActions.PLAY;
  const params = {
    action,
    source: StatsConstants.StatsSources.WEB_PLAYBACK_CONTROLS,
    userInitiated: playbackActionsAnalyticsUserInitiated,
  };
  yield call(logPlaybackInteractions, params);
}

export function* onOpenDropdown({ payload: { dropdownConfiguration } }) {
  if (!dropdownConfiguration || !dropdownConfiguration[1].viewMode) {
    return;
  }

  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const eventData = yield call(getPageView, currentPath);

  if (!eventData) return;

  eventData.view_mode = dropdownConfiguration[1].viewMode;

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_WEB_VIEW_MODE,
      eventData,
    },
  ]);
}

export function* logBusinessesEvent({ payload }) {
  const { eventData } = payload;

  if (!eventData) {
    return;
  }
  let customEventData = {};

  if (!eventData.page_view) {
    const defaultEventData = yield select(StatsUtils.getDefaultEventData);
    const tier = yield select(D.Selectors.Payments.getSelectedTier);
    const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
    const pageView = yield call(getPageView, currentPath);
    if (!pageView) {
      return;
    }

    customEventData = {
      ...eventData,
      device_model: defaultEventData[StatsConstants.DefaultFieldNames.BROWSER],
      item_id: 'footer_navlink',
      item_text_title: 'Businesses',
      item_type: 'navlink',
      page_view: pageView.page_view,
      tier,
      view_mode: 'footer_navlink',
    };
  }

  yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_UNIVERSAL_COMPONENT_ACTIONS,
      eventData: customEventData,
    },
  ]);
}

export function* logCommunityEvent({ payload }) {
  const { eventData } = payload;
  if (!eventData) {
    return;
  }

  if (!eventData.page_view) {
    const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
    const pageView = yield call(getPageView, currentPath);
    if (!pageView) {
      return; // don't log event if there's no page_view
    }
    eventData.page_view = pageView.page_view;
  }

  yield D.tryCall(logEvents, [
    {
      eventName: D.Constants.Community.COMMUNITY_WEB_ACTION,
      eventData,
    },
  ]);
}

function getSourceCardParentTypeFromPandoraType(pandoraType) {
  switch (pandoraType) {
    case D.Constants.SourceTypes.PandoraTypes.ALBUM:
      return 'album';
    case D.Constants.SourceTypes.PandoraTypes.PLAYLIST:
      return 'playlist';
    case D.Constants.SourceTypes.PandoraTypes.STATION:
      return 'station';
    case D.Constants.SourceTypes.PandoraTypes.PODCAST:
      return 'podcast';
    default:
      return pandoraType ? 'other' : null;
  }
}

export function* onSourceCardEvent(action) {
  const { optionName, stationId, isDisabled } = action.payload;
  let { pandoraId, pandoraType, parentId, parentType } = action.payload;

  let actionType = StatsConstants.mapActionTypeToStatsActionType[optionName];
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const pageViewData = (yield call(getPageView, currentPath)) || {};

  if (action.type === D.Constants.SourceCard.ActionTypes.SOURCE_CARD_OPENED) {
    actionType = StatsConstants.StatsActions.OPEN;
  }
  if (pandoraType === D.Constants.SourceTypes.PandoraTypes.STATION) {
    // For stations we always want stationId. stationId and parentId are more
    // likely to be station ID, based on current source card configurations.
    pandoraId = stationId || parentId || pandoraId;
  }
  if (!parentId) {
    parentId = yield select(D.Utils.Entity.getParentIdByChildId, pandoraId);
    parentType = parentId && D.Utils.SourceType.getPandoraTypeFromPandoraId(parentId);
  }
  if (!parentId) {
    // If we still don't have a parentId analytics wants the child info
    parentId = pandoraId;
    parentType = pandoraType;
  }
  pandoraType = StatsConstants.MapSourceTypeToSourceCardType[pandoraType] || null;
  parentType = getSourceCardParentTypeFromPandoraType(parentType);

  return yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.SOURCE_CARD,
      eventData: {
        action: actionType,
        is_disabled: !!isDisabled,
        page_view: pageViewData.page_view,
        parent_type: parentType,
        play_source_id: parentId,
        source_music_id: pandoraId,
        source_type: pandoraType,
        view_mode: pageViewData.view_mode,
      },
    },
  ]);
}

export function* logApsError(action) {
  const { apsEndpoint } = action.data;
  switch (apsEndpoint) {
    case D.Constants.Api.ApiEndpoints.PLAYBACK_SOURCE:
    case D.Constants.Api.ApiEndpoints.PLAYBACK_CURRENT:
      yield call(logPlaybackInteractions, {
        action: StatsConstants.StatsActions.APS_PLAYBACK_ERROR,
      });
      break;
    case D.Constants.Api.ApiEndpoints.EVENT_STARTED:
    case D.Constants.Api.ApiEndpoints.EVENT_PROGRESS:
    case D.Constants.Api.ApiEndpoints.EVENT_ENDED:
      yield call(logPlaybackInteractions, {
        action: StatsConstants.StatsActions.APS_EVENT_ERROR,
      });
      break;
    case D.Constants.Api.ApiEndpoints.ACTION_REPLAY:
    case D.Constants.Api.ApiEndpoints.ACTION_THUMB_UP:
    case D.Constants.Api.ApiEndpoints.ACTION_THUMB_DOWN:
    case D.Constants.Api.ApiEndpoints.ACTION_REMOVE_THUMB:
    case D.Constants.Api.ApiEndpoints.ACTION_SKIP:
    case D.Constants.Api.ApiEndpoints.ACTION_PAUSE:
    case D.Constants.Api.ApiEndpoints.ACTION_REPEAT:
    case D.Constants.Api.ApiEndpoints.ACTION_SHUFFLE:
      yield call(logPlaybackInteractions, {
        action: StatsConstants.StatsActions.APS_ACTION_ERROR,
      });
      break;
  }
}

export function getPACoachmarkDismissEventReason(action) {
  const { START_STATION, START_TRIAL, UPGRADE } = D.Constants.External.CoachmarkDismissReasons;
  let reason = StatsExternals.CoachmarkReasons.DISMISS;
  switch (getIn(action, 'data.reason')) {
    case START_STATION:
      reason = StatsExternals.CoachmarkReasons.START_STATION;
      break;
    case START_TRIAL:
    case UPGRADE:
      reason = StatsExternals.CoachmarkReasons.UPSELL;
      break;
    default:
      break;
  }
  return reason;
}

export function* onSetOpenCoachmark(action) {
  const CoachmarkConstants = D.getInDomain('rewards.constants.CoachmarkConstants');
  const coachmarkType = getIn(action, 'data.type');
  switch (coachmarkType) {
    case CoachmarkConstants.PREMIUM_ACCESS_COACHMARK:
      yield call(logOpenedPremiumAccessCoachmark, action);
      break;
    case CoachmarkConstants.PA_CAP_COACHMARK:
      yield call(onPremiumAccessCapEvent, null, false);
      break;
  }
}

export function* onCloseRewardsCoachmark(action) {
  const CoachmarkConstants = D.getInDomain('rewards.constants.CoachmarkConstants');
  const premiumAccessCoachmarkData = yield call(getPremiumAccessCoachmarkData);
  if (premiumAccessCoachmarkData) {
    const coachmarkType = premiumAccessCoachmarkData.get('type');
    switch (coachmarkType) {
      case CoachmarkConstants.PREMIUM_ACCESS_COACHMARK:
        yield call(logDismissedPremiumAccessCoachmark, action);
        break;
      case CoachmarkConstants.PA_CAP_COACHMARK: {
        const reason = getPACoachmarkDismissEventReason(action);
        yield call(onPremiumAccessCapEvent, reason, true);
        break;
      }
    }
  }
}

export function* onPremiumAccessCapEvent(reason, clicked) {
  const eventName = StatsConstants.Events.COACHMARK;
  const premiumAccessCoachmarkData = yield call(getPremiumAccessCoachmarkData);
  const coachmarkType = premiumAccessCoachmarkData.get('type').toLowerCase();
  const context = premiumAccessCoachmarkData.get('context');
  const openedFromSearch =
    !!context && context === D.Constants.Ads.Rewards.ContextTypes.SEARCH_PLAY;
  const source = premiumAccessCoachmarkData.get('target');
  const coachmarkId = StatsUtils.getCoachmarkId(source, openedFromSearch);
  const eventData = {
    coachmark_type: coachmarkType,
    coachmark_id: coachmarkId,
    reason,
    clicked,
  };
  yield D.tryCall(logEvents, [
    {
      eventName,
      eventData,
    },
  ]);
}

const mapCoachmarkTypeToStatsCoachmarkID = Object.freeze({
  [D.Constants.MiniCoachmark.MiniCoachmarkTypes.FIRST_TIME_PODCAST_NOW_PLAYING]: [
    StatsExternals.CoachmarkIds.PODCAST_COLLECT_NOW_PLAYING,
  ],
  [D.Constants.MiniCoachmark.MiniCoachmarkTypes.FIRST_TIME_PODCAST_COLLECTIONS]: [
    StatsExternals.CoachmarkIds.PODCAST_FILTER,
  ],
  [D.Constants.MiniCoachmark.MiniCoachmarkTypes.THUMB_UP]: [StatsExternals.CoachmarkIds.TOAST_UP],
  [D.Constants.MiniCoachmark.MiniCoachmarkTypes.THUMB_DOWN]: [
    StatsExternals.CoachmarkIds.TOAST_DOWN,
  ],
});

export function* logFTUXCoachmarkViewed({ payload }) {
  const coachmarkId = mapCoachmarkTypeToStatsCoachmarkID[payload.coachmarkType];
  if (coachmarkId) {
    const eventData = {
      coachmark_type: StatsConstants.CoachmarkTypes.PODCAST,
      coachmark_id: coachmarkId[0],
      reason: null,
      clicked: false,
    };
    yield D.tryCall(logEvents, [
      {
        eventName: StatsConstants.Events.COACHMARK,
        eventData,
      },
    ]);
  }
}

export function* logFTUXCoachmarkClicked({ payload: { coachmarkType, reason } }) {
  const coachmarkId = mapCoachmarkTypeToStatsCoachmarkID[coachmarkType];
  if (coachmarkId) {
    const eventData = {
      coachmark_type: StatsConstants.CoachmarkTypes.PODCAST,
      coachmark_id: coachmarkId[0],
      reason,
      clicked: true,
    };
    yield D.tryCall(logEvents, [
      {
        eventName: StatsConstants.Events.COACHMARK,
        eventData,
      },
    ]);
  }
}

export function* logBrowseClickEvent({ payload: { componentEventType, analyticsToken } }) {
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const isBrowseEvent = D.Utils.Route.isBrowsePath(currentPath);

  if (!isBrowseEvent) {
    return;
  }

  const sessionId = yield select(StatsUtils.getBrowseSessionId);
  let actionType = StatsConstants.mapActionTypeToStatsActionType[componentEventType];
  actionType = actionType || StatsConstants.MapNavigationTypeToStatsActionType[componentEventType];

  if (!actionType) {
    return;
  }

  return yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.WEB_BROWSE_SELECT,
      eventData: {
        resulting_action: actionType,
        analytics_token: analyticsToken,
        session_id: sessionId,
      },
    },
  ]);
}

export function* logImageFetchError(action) {
  const appVersion = yield select(D.Utils.DebugSettings.version);
  const deviceOs = yield select(D.Utils.ClientInfo.getDeviceOS);
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const pageViewData = (yield call(getPageView, currentPath)) || {};
  const browser = yield select(D.Utils.ClientInfo.getSimplifiedDeviceOS);
  const { pandoraId, stationId, musicId, genreCategoryToken, id, entityIdentifier } =
    action.payload;
  const parsedId =
    pandoraId || stationId || musicId || genreCategoryToken || id || entityIdentifier;

  const eventData = {
    appVersion,
    asset_url: action.payload.asset_url,
    browser,
    day: Date.now().toString(),
    deviceOs,
    errorCode: action.payload.errorCode,
    listenerId: +action.sharedState.listenerId,
    pageView: pageViewData.page_view,
    pandora_id: parsedId,
    vendorId: 100,
    viewMode: pageViewData.view_mode,
  };

  return yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_IMAGE_FETCH_ERROR,
      eventData,
    },
  ]);
}

export function* logStartPlayingEvent() {
  const isAutoStartEnabled = yield select(D.getInDomain('playback.utils.isAutoStartEnabled'));
  const isUserAuth = yield select(D.Utils.Listener.wasAuthUserInitiated);
  const isAnonymousListener = yield select(D.Utils.Listener.isAnonymousListener);

  if (!isUserAuth && isAutoStartEnabled && !isAnonymousListener) {
    yield call(D.Utils.Time.recordTimeAtMoment, D.Constants.App.TimingMoments.READY_TO_PLAY);
    yield call(logAppTimings, D.Utils.Time.getReadyToPlayTimingMeasurement());
  }
}

function* getAudioPlayerTTAData(track) {
  const newTrackUrl = track.get('audioURL');
  const key = track.get('key');
  let trackFormat = track.get('audioEncoding');

  // Only encrypted audio will include '_frag' suffix
  if (key && ['mp4', 'm4a'].includes(trackFormat)) {
    const isSXMEncryptedStationUnfrag = yield select(D.Utils.Listener.isSXMEncryptedStationUnfrag);
    trackFormat =
      track.get('stationId') && isSXMEncryptedStationUnfrag
        ? `${trackFormat}_unfrag`
        : `${trackFormat}_frag`;
  }

  return {
    encryption_format: key ? 'bitmask' : 'unencrypted',
    audio_encoding_format: trackFormat,
    backgrounded: D.Utils.PageVisibility.isHidden(),
    content_host: D.Utils.AudioPlayerUtils.getAudioContentHost(newTrackUrl),
  };
}

export function* logAudioPlayerTTAEvent({ payload: { track } }) {
  const newTrackUrl = track.get('audioURL');
  const activeTrackUrl = yield select(D.getInDomain('playback.utils.getActiveTrackUrl'));

  // This condition ensures the ttm event is only fired once per audio source, when the source initially starts to play.
  // Also, we decide not to log TTA for initial playback (activeTrackUrl === null)
  // cause if initial audio playback is disabled that the time to audio will be really long
  if (activeTrackUrl !== null && activeTrackUrl !== newTrackUrl) {
    const data = yield call(getAudioPlayerTTAData, track);

    yield take(D.Constants.Playback.ActionTypes.PLAYBACK_LOG_START_PLAYING);
    D.Utils.Time.markTimeAtMoment(D.Constants.App.PlaybackMarks.AUDIO_PLAY);
    const playingAction = yield take(D.Constants.AudioPlayer.AudioPlayerActions.PLAYING);
    D.Utils.Time.markTimeAtMoment(D.Constants.App.PlaybackMarks.AUDIO_PLAYING);
    data.time_measurement = D.Utils.Time.getAudioPlayerTimeToAudioTimingMeasurement();

    if (playingAction.payload.isPreload) {
      yield call(logAudioPlayerTTAPreloaded, data);
    } else {
      yield call(logAudioPlayerTTA, data);
    }
  }
}

export function* logGraphQLDuration({ payload: { duration, uiMode, viewMode } }) {
  return yield D.tryCall(logEvents, [
    {
      eventName: StatsConstants.Events.EVENT_WEB_VIEW_MODE,
      eventData: {
        ui_mode: uiMode,
        view_mode: viewMode,
        [StatsConstants.PerformanceKeys.TTUI_MSEC]: duration,
      },
    },
  ]);
}

export function* logAudioPlayerError({ payload }) {
  if (!D.Constants.AudioPlayer.RecoverableErrors.includes(payload.ERROR_OBJ.commonType)) {
    return;
  }

  const hasErrorCountExceededMax = yield select(
    D.getInDomain('playback.utils.hasAudioRecoverableErrorCountExceededMax'),
  );
  if (!hasErrorCountExceededMax && !payload.isPreload) {
    const { timeout } = yield race({
      playbackCurrent: take(D.Constants.Api.ApiActions.PLAYBACK_CURRENT_ATTEMPT),
      getFragment: take(D.Constants.Api.ApiActions.GET_FRAGMENT_ATTEMPT),
      timeout: delay(3000),
    });

    if (timeout) {
      log.warn('No current or getFragment call after logAudioPlayerError', payload);
    }
  }
}

export function* updatePreviousPlayedHarness({ payload }) {
  yield put(StatsActions.updatePreviousPlayedHarness(payload));
}

export function* logAudioAdSkippabilityExposureEvent() {
  yield call(
    logAbTreatmentArmExposureEvent,
    D.Constants.Experiments.EXPERIMENT_GROUPS.webAudioAdsSkippability,
    D.Constants.Experiments.TREATMENT_ARMS.audioAdsSkippabilityEnabled,
  );
}

export function* logWebPingRadioHealthExposureEvent() {
  yield call(
    logAbTreatmentArmExposureEvent,
    D.Constants.Experiments.EXPERIMENT_GROUPS.webPingRadioHealth,
    D.Constants.Experiments.TREATMENT_ARMS.webPingRadioHealth,
  );
}

function ttmSourceChangedAction(action) {
  return (
    action.type === StatsConstants.StatsActions.TTM_START &&
    action.data.get('ttmAction') === StatsConstants.TTMActions.SOURCE_CHANGED
  );
}

function audioPlayTrackOnDemandAction(action) {
  return (
    action.type === D.Constants.AudioPlayer.AudioPlayerActions.AUDIO_PLAY_TRACK &&
    action.payload.playlistId
  );
}

export function* startDetectorLogging() {
  const isDetectorTrackMetadataEnabled = yield select(
    D.Utils.Listener.isDetectorTrackMetadataEnabled,
  );
  if (isDetectorTrackMetadataEnabled) {
    yield call(
      logAbTreatmentArmExposureEvent,
      D.Constants.Experiments.EXPERIMENT_GROUPS.webAppIntegrity,
      D.Constants.Experiments.TREATMENT_ARMS.detectorTrackMetadata,
    );
    yield fork(logDetectorAudioInfo);
  }
}

export function* logDetectorAudioInfo() {
  while (LOOP) {
    const action = yield take(audioPlayTrackOnDemandAction);
    const { track } = action.data;
    const pandoraId = track.get('pandoraId', null);
    const annotations = yield select(D.getInDomain('catalog.utils.getAnnotations'), pandoraId);
    yield call(Detector.update, {
      listenerId: getIn(action, 'sharedState.listenerId', null),
      r1: pandoraId,
      r2: track.get('sourceId', null),
      r3: track.get('artistName', null),
      r4: track.get('albumName', null),
      r5: track.get('songName', null),
      r6: track.get('duration', null),
      r7: annotations.get('artistId', null),
      r8: annotations.get('albumId', null),
      r9: annotations.get('shareableUrlPath', null),
    });
    yield call(Detector.report, { eventType: '7' });
    yield take(ttmSourceChangedAction);
  }
}

export function* logOmSdkExposureEvent() {
  yield call(
    logAbTreatmentArmExposureEvent,
    D.Constants.Experiments.EXPERIMENT_GROUPS.measurementThroughOmsdk,
    D.Constants.Experiments.TREATMENT_ARMS.measurementThroughOmsdk,
  );
}

export function* logApsMM4ExposureEvent() {
  yield call(
    logAbTreatmentArmExposureEvent,
    D.Constants.Experiments.EXPERIMENT_GROUPS.webAPSMigrationMM4,
    D.Constants.Experiments.TREATMENT_ARMS.webAPSMigrationMM4,
  );
}
